import {
  Activity,
  AlertTriangle,
  BellRing,
  Bot,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Code2,
  Coins,
  Copy,
  Command,
  FileCode2,
  FileImage,
  Film,
  Gift,
  ExternalLink,
  KeyRound,
  LayoutDashboard,
  ListFilter,
  LogOut,
  Menu,
  MessageSquareText,
  MonitorUp,
  Pencil,
  Play,
  PlugZap,
  Plus,
  Radio,
  RefreshCw,
  Save,
  ScrollText,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Trophy,
  Trash2,
  Upload,
  UsersRound,
  WandSparkles,
  Wifi,
  X,
  Zap,
} from "lucide-react";
import { useEffect, useMemo, useState, type CSSProperties, type FormEvent, type ReactNode } from "react";
import type {
  AlertEventType,
  AlertTemplateRecord,
  CommandRecord,
  CustomOverlayRecord,
  CustomOverlayAssetRecord,
  CustomOverlaySummary,
  DashboardSection,
  DashboardSummary,
  FlowAction,
  FlowCondition,
  FlowRecord,
  FlowTrigger,
  GiveawayRecord,
  ModerationIncidentRecord,
  ModerationRuleRecord,
  RedemptionRecord,
  RewardHandlingMode,
  RewardRecord,
  SnackLedgerRecord,
  SnackLeaderboardRow,
  SnacksSummary,
  ServiceState,
  TimerRecord,
  VariableRecord,
  ViewerProfileRecord,
  ViewerRecord,
  WidgetRecord,
  WidgetType,
} from "@thesleepybot/shared";
import { dashboardSections } from "@thesleepybot/shared";
import { api, apiBaseUrl, apiUpload, ApiError } from "./api";
import { demoAlertTemplates, demoCommands, demoFlows, demoGiveaways, demoLeaders, demoModerationIncidents, demoModerationRules, demoRedemptions, demoRewards, demoSnackLedger, demoSnacksSummary, demoSummary, demoTimers, demoVariables, demoViewerProfile, demoViewers, demoWidgets } from "./demo";
import { OperationalLogsPage } from "./OperationalLogsPage";

type AuthState =
  | { mode: "checking" }
  | { mode: "signed-out"; canBootstrap: boolean }
  | { mode: "signed-in"; username: string; preview: boolean };

const sectionIcons: Record<DashboardSection, typeof LayoutDashboard> = {
  Dashboard: LayoutDashboard,
  Commands: Command,
  Timers: Clock3,
  Moderation: ShieldCheck,
  Viewers: UsersRound,
  SleepySnacks: Coins,
  Rewards: Gift,
  Giveaways: Trophy,
  SleepyFlow: WandSparkles,
  "Alert Studio": BellRing,
  "OBS Widgets": MonitorUp,
  Logs: ScrollText,
  Settings,
};

function formatAgo(value: string | null): string {
  if (!value) return "Never";
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86_400)}d ago`;
}

const disconnectedSummary: DashboardSummary = {
  channel: {
    kickConnected: false,
    live: false,
    botActive: false,
    offlineTestMode: false,
    slug: null,
    title: null,
    viewers: 0,
  },
  health: {
    api: "attention",
    kickEvents: "offline",
    queue: "attention",
    realtime: "attention",
    lastEventAt: null,
    reconnects: 0,
  },
  activity: [],
};

function App() {
  if (window.location.pathname.startsWith("/overlay/")) return <OverlayPage />;
  return <ControlStudio />;
}

function ControlStudio() {
  const [auth, setAuth] = useState<AuthState>({ mode: "checking" });

  useEffect(() => {
    const preview = import.meta.env.DEV && new URLSearchParams(window.location.search).get("preview") === "1";
    if (preview) {
      setAuth({ mode: "signed-in", username: "SleepyKevy", preview: true });
      return;
    }

    api<{ username: string }>("/api/auth/me")
      .then((user) => setAuth({ mode: "signed-in", username: user.username, preview: false }))
      .catch(async (error: unknown) => {
        if (error instanceof ApiError && error.status === 401) {
          const setup = await api<{ canBootstrap: boolean }>("/api/auth/setup-status").catch(() => ({ canBootstrap: false }));
          setAuth({ mode: "signed-out", canBootstrap: setup.canBootstrap });
          return;
        }
        setAuth({ mode: "signed-out", canBootstrap: false });
      });
  }, []);

  if (auth.mode === "checking") return <LoadingScreen />;
  if (auth.mode === "signed-out") {
    return <AuthScreen canBootstrap={auth.canBootstrap} onSignedIn={(username) => setAuth({ mode: "signed-in", username, preview: false })} />;
  }

  return <Studio username={auth.username} preview={auth.preview} onSignOut={() => setAuth({ mode: "signed-out", canBootstrap: false })} />;
}

function LoadingScreen() {
  return (
    <main className="loading-screen">
      <img src="/assets/thesleepybot-mark.png" alt="" />
      <div className="loading-line" />
      <span>Opening the studio</span>
    </main>
  );
}

function AuthScreen({ canBootstrap, onSignedIn }: { canBootstrap: boolean; onSignedIn: (username: string) => void }) {
  const [mode, setMode] = useState<"login" | "bootstrap">(canBootstrap ? "bootstrap" : "login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [bootstrapToken, setBootstrapToken] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const path = mode === "bootstrap" ? "/api/auth/bootstrap" : "/api/auth/login";
      const user = await api<{ username: string }>(path, {
        method: "POST",
        body: JSON.stringify({ username, password, bootstrapToken: mode === "bootstrap" ? bootstrapToken : undefined }),
      });
      onSignedIn(user.username);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Sign-in failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-shell">
      <section className="auth-brand">
        <div className="auth-brand-top">
          <img src="/assets/thesleepybot-wordmark.png" alt="TheSleepyBot" />
          <span className="private-badge"><KeyRound size={13} /> Owner-only build</span>
        </div>
        <div className="auth-copy">
          <p className="eyebrow">Cloud creator control</p>
          <h1>Your stream’s quiet operator.</h1>
          <p>Commands, moderation, SleepySnacks, automations, alerts, and OBS widgets—ready when you go live.</p>
        </div>
        <div className="auth-signal"><span /><span /><span /> KICK-FIRST SYSTEM</div>
      </section>

      <section className="auth-panel">
        <div className="auth-form-wrap">
          <img className="auth-mark" src="/assets/thesleepybot-mark.png" alt="" />
          <p className="eyebrow">{mode === "bootstrap" ? "First private setup" : "Sleepy studio"}</p>
          <h2>{mode === "bootstrap" ? "Create your owner login" : "Welcome back"}</h2>
          <p className="subtle">{mode === "bootstrap" ? "This one-time step locks the dashboard to your account." : "Sign in with your TheSleepyBot username and password."}</p>
          <form onSubmit={submit}>
            <label>Username<input value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="username" minLength={3} maxLength={32} required /></label>
            <label>Password<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete={mode === "bootstrap" ? "new-password" : "current-password"} minLength={12} maxLength={128} required /></label>
            {mode === "bootstrap" && <label>Private setup token<input type="password" value={bootstrapToken} onChange={(event) => setBootstrapToken(event.target.value)} autoComplete="off" required /></label>}
            {error && <div className="form-error"><AlertTriangle size={16} />{error}</div>}
            <button className="primary-button auth-button" type="submit" disabled={busy}>{busy ? <RefreshCw className="spin" size={18} /> : <KeyRound size={18} />}{busy ? "Checking…" : mode === "bootstrap" ? "Create owner account" : "Enter the studio"}</button>
          </form>
          {canBootstrap && <button className="text-button" onClick={() => setMode(mode === "login" ? "bootstrap" : "login")}>{mode === "login" ? "Create the first owner account" : "I already created my account"}</button>}
        </div>
      </section>
    </main>
  );
}

function Studio({ username, preview, onSignOut }: { username: string; preview: boolean; onSignOut: () => void }) {
  const [section, setSection] = useState<DashboardSection>("Dashboard");
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className={`studio ${collapsed ? "sidebar-collapsed" : ""}`}>
      <Sidebar section={section} collapsed={collapsed} mobileOpen={mobileOpen} onSelect={(next) => { setSection(next); setMobileOpen(false); }} onCollapse={() => setCollapsed(!collapsed)} onClose={() => setMobileOpen(false)} />
      <main className="workspace">
        <header className="topbar">
          <button className="icon-button mobile-menu" aria-label="Open navigation" onClick={() => setMobileOpen(true)}><Menu size={20} /></button>
          <div><p className="eyebrow">Control studio</p><h1>{section}</h1></div>
          <div className="topbar-actions">
            {preview && <span className="preview-badge">LOCAL PREVIEW</span>}
            <span className="live-chip"><span /> Live system</span>
            <button className="profile-button"><span>{username.slice(0, 1).toUpperCase()}</span><div><strong>{username}</strong><small>Owner</small></div></button>
            <button className="icon-button" title="Sign out" onClick={async () => { if (!preview) await api<{ signedOut: true }>("/api/auth/logout", { method: "POST", body: "{}" }).catch(() => undefined); onSignOut(); }}><LogOut size={18} /></button>
          </div>
        </header>
        <div className="page"><SectionContent section={section} preview={preview} /></div>
      </main>
    </div>
  );
}

function Sidebar({ section, collapsed, mobileOpen, onSelect, onCollapse, onClose }: { section: DashboardSection; collapsed: boolean; mobileOpen: boolean; onSelect: (section: DashboardSection) => void; onCollapse: () => void; onClose: () => void }) {
  return (
    <>
      {mobileOpen && <button className="sidebar-scrim" aria-label="Close navigation" onClick={onClose} />}
      <aside className={`sidebar ${mobileOpen ? "mobile-open" : ""}`}>
        <div className="sidebar-brand">
          <img className="wordmark" src="/assets/thesleepybot-wordmark.png" alt="TheSleepyBot" />
          <img className="mark" src="/assets/thesleepybot-mark.png" alt="TheSleepyBot" />
          <button className="icon-button mobile-close" aria-label="Close navigation" onClick={onClose}><X size={18} /></button>
        </div>
        <nav>
          {dashboardSections.map((item) => {
            const Icon = sectionIcons[item];
            return <button key={item} className={section === item ? "active" : ""} title={collapsed ? item : undefined} onClick={() => onSelect(item)}><Icon size={18} /><span>{item}</span>{section === item && <i />}</button>;
          })}
        </nav>
        <div className="sidebar-footer">
          <div className="bot-state"><span className="status-orb"><Bot size={16} /></span><div><strong>Bot engine</strong><small>Ready for live</small></div></div>
          <button className="collapse-button" onClick={onCollapse}>{collapsed ? <ChevronRight size={18} /> : <><ChevronLeft size={18} /><span>Collapse studio</span></>}</button>
        </div>
      </aside>
    </>
  );
}

function SectionContent({ section, preview }: { section: DashboardSection; preview: boolean }) {
  switch (section) {
    case "Dashboard": return <DashboardHome preview={preview} />;
    case "Commands": return <CommandsPage preview={preview} />;
    case "Timers": return <TimersPage preview={preview} />;
    case "Moderation": return <ModerationPage preview={preview} />;
    case "Viewers": return <ViewersPage preview={preview} />;
    case "SleepySnacks": return <SnacksPage preview={preview} />;
    case "Rewards": return <RewardsPage preview={preview} />;
    case "Giveaways": return <GiveawaysPage preview={preview} />;
    case "SleepyFlow": return <SleepyFlowPage preview={preview} />;
    case "Alert Studio": return <AlertStudioPage preview={preview} />;
    case "OBS Widgets": return <WidgetsPage preview={preview} />;
    case "Logs": return <OperationalLogsPage preview={preview} />;
    case "Settings": return <SettingsPage preview={preview} />;
  }
}

function DashboardHome({ preview }: { preview: boolean }) {
  const [summary, setSummary] = useState<DashboardSummary>(preview ? demoSummary : disconnectedSummary);
  const [syncing, setSyncing] = useState(!preview);

  async function refreshSummary() {
    if (preview) return;
    setSyncing(true);
    try {
      setSummary(await api<DashboardSummary>("/api/dashboard"));
    } finally {
      setSyncing(false);
    }
  }

  useEffect(() => {
    if (preview) return;
    void refreshSummary().catch(() => undefined);
  }, [preview]);

  const states = useMemo(() => [summary.health.api, summary.health.kickEvents, summary.health.queue, summary.health.realtime], [summary]);
  const healthy = states.filter((state) => state === "online").length;

  return (
    <div className="dashboard-grid">
      <section className="section-block span-12">
        <SectionHeading eyebrow="Live control" title="Kick connection & bot status" action={<button className="secondary-button" disabled={syncing} onClick={() => void refreshSummary().catch(() => undefined)}><RefreshCw className={syncing ? "spin" : ""} size={16} /> Refresh</button>} />
        <div className="status-grid">
          <StatusCard icon={<PlugZap />} label="Kick connection" value={summary.channel.kickConnected ? `@${summary.channel.slug}` : "Not connected"} state={summary.channel.kickConnected ? "online" : "offline"} detail={summary.channel.kickConnected ? "OAuth and webhook subscriptions active" : "Connect Kick in Settings"} />
          <StatusCard icon={<Radio />} label="Channel" value={summary.channel.live ? "Live now" : "Offline"} state={summary.channel.live ? "online" : "offline"} detail={summary.channel.live ? `${summary.channel.viewers.toLocaleString()} viewers · ${summary.channel.title}` : "The bot sleeps until your next stream"} />
          <StatusCard icon={<Bot />} label="TheSleepyBot" value={summary.channel.botActive ? "Active in chat" : "Standing by"} state={summary.channel.botActive ? "online" : "attention"} detail={summary.channel.botActive ? "Commands, timers, moderation and events enabled" : "Automatically starts when the channel is live"} />
        </div>
      </section>

      <section className="panel span-8 activity-panel">
        <SectionHeading eyebrow="Latest signals" title="Recent chat & moderation" action={<button className="text-button">Open full history <ChevronRight size={15} /></button>} />
        <div className="activity-list">
          {summary.activity.map((item) => <div className="activity-row" key={item.id}><span className={`activity-icon ${item.kind}`}>{item.kind === "chat" ? <MessageSquareText /> : item.kind === "moderation" ? <ShieldCheck /> : item.kind === "event" ? <Sparkles /> : <Activity />}</span><div><p><strong>{item.actor}</strong> {item.text}</p><small>{formatAgo(item.createdAt)}</small></div>{item.severity === "warning" && <span className="severity">REVIEWED</span>}</div>)}
        </div>
      </section>

      <section className="panel span-4 health-panel">
        <SectionHeading eyebrow="Cloud runtime" title="System health" />
        <div className="health-score"><div className="health-ring" style={{ "--score": `${healthy * 25}%` } as React.CSSProperties}><strong>{healthy}/4</strong><small>healthy</small></div><div><strong>All core services</strong><p>{summary.health.reconnects === 0 ? "No errors or reconnects this stream." : `${summary.health.reconnects} reconnects this stream.`}</p></div></div>
        <div className="health-list"><HealthRow label="Dashboard API" state={summary.health.api} /><HealthRow label="Kick event stream" state={summary.health.kickEvents} /><HealthRow label="Event queue" state={summary.health.queue} /><HealthRow label="OBS realtime" state={summary.health.realtime} /></div>
        <div className="last-signal"><Wifi size={15} /> Last Kick signal {formatAgo(summary.health.lastEventAt)}</div>
      </section>
    </div>
  );
}

function StatusCard({ icon, label, value, detail, state }: { icon: ReactNode; label: string; value: string; detail: string; state: ServiceState }) {
  return <article className="status-card"><div className={`status-card-icon ${state}`}>{icon}</div><div className="status-card-title"><span>{label}</span><StatePill state={state} /></div><h3>{value}</h3><p>{detail}</p></article>;
}

function StatePill({ state }: { state: ServiceState }) {
  return <span className={`state-pill ${state}`}><i />{state}</span>;
}

function HealthRow({ label, state }: { label: string; state: ServiceState }) {
  return <div><span>{label}</span><StatePill state={state} /></div>;
}

function CommandsPage({ preview }: { preview: boolean }) {
  const [commands, setCommands] = useState<CommandRecord[]>(preview ? demoCommands : []);
  const [variables, setVariables] = useState<VariableRecord[]>(preview ? demoVariables : []);
  const [composer, setComposer] = useState<{ mode: "create" } | { mode: "edit"; command: CommandRecord } | null>(null);
  const [variableComposer, setVariableComposer] = useState<{ mode: "create" } | { mode: "edit"; variable: VariableRecord } | null>(null);
  const [loading, setLoading] = useState(!preview);
  const [saving, setSaving] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState("");

  async function refreshCommands() {
    if (preview) return;
    setLoading(true);
    setError("");
    try {
      const [nextCommands, nextVariables] = await Promise.all([
        api<CommandRecord[]>("/api/commands"),
        api<VariableRecord[]>("/api/variables"),
      ]);
      setCommands(nextCommands);
      setVariables(nextVariables);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load commands.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (preview) return;
    void refreshCommands();
  }, [preview]);

  async function saveCommand(draft: Omit<CommandRecord, "id" | "uses">) {
    const editing = composer?.mode === "edit" ? composer.command : null;
    if (preview) {
      setCommands((items) => (editing
        ? items.map((item) => item.id === editing.id ? { ...editing, ...draft } : item)
        : [...items, { id: crypto.randomUUID(), ...draft, uses: 0 }]
      ).sort((a, b) => a.name.localeCompare(b.name)));
      setComposer(null);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const saved = await api<CommandRecord>(editing ? `/api/commands/${editing.id}` : "/api/commands", { method: editing ? "PATCH" : "POST", body: JSON.stringify(draft) });
      setCommands((items) => (editing
        ? items.map((item) => item.id === editing.id ? saved : item)
        : [...items, saved]
      ).sort((a, b) => a.name.localeCompare(b.name)));
      setComposer(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save the command.");
    } finally {
      setSaving(false);
    }
  }

  async function toggleCommand(command: CommandRecord) {
    if (preview) {
      setCommands((items) => items.map((item) => item.id === command.id ? { ...item, enabled: !item.enabled } : item));
      return;
    }
    setUpdatingId(command.id);
    setError("");
    try {
      const updated = await api<CommandRecord>(`/api/commands/${command.id}`, { method: "PATCH", body: JSON.stringify({ enabled: !command.enabled }) });
      setCommands((items) => items.map((item) => item.id === command.id ? updated : item));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not update the command.");
    } finally {
      setUpdatingId(null);
    }
  }

  async function deleteCommand(command: CommandRecord) {
    if (preview) {
      setCommands((items) => items.filter((item) => item.id !== command.id));
      return;
    }
    setDeletingId(command.id);
    setError("");
    try {
      await api<{ deleted: boolean }>(`/api/commands/${command.id}`, { method: "DELETE" });
      setCommands((items) => items.filter((item) => item.id !== command.id));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not delete the command.");
    } finally {
      setDeletingId(null);
    }
  }

  async function saveVariable(draft: Omit<VariableRecord, "id">) {
    const editing = variableComposer?.mode === "edit" ? variableComposer.variable : null;
    if (preview) {
      setVariables((items) => (editing
        ? items.map((item) => item.id === editing.id ? { ...editing, ...draft } : item)
        : [...items, { id: crypto.randomUUID(), ...draft }]
      ).sort((a, b) => a.name.localeCompare(b.name)));
      setVariableComposer(null);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const saved = await api<VariableRecord>(editing ? `/api/variables/${editing.id}` : "/api/variables", { method: editing ? "PATCH" : "POST", body: JSON.stringify(draft) });
      setVariables((items) => (editing
        ? items.map((item) => item.id === editing.id ? saved : item)
        : [...items, saved]
      ).sort((a, b) => a.name.localeCompare(b.name)));
      setVariableComposer(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save the variable.");
    } finally {
      setSaving(false);
    }
  }

  async function deleteVariable(variable: VariableRecord) {
    if (preview) {
      setVariables((items) => items.filter((item) => item.id !== variable.id));
      return;
    }
    setDeletingId(variable.id);
    setError("");
    try {
      await api<{ deleted: boolean }>(`/api/variables/${variable.id}`, { method: "DELETE" });
      setVariables((items) => items.filter((item) => item.id !== variable.id));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not delete the variable.");
    } finally {
      setDeletingId(null);
    }
  }

  return <ModulePage eyebrow="Chat toolkit" title="Custom commands" description="Build responses with variables, aliases, permission levels, enforced cooldowns, and optional SleepySnacks costs." action={<button className="primary-button" onClick={() => setComposer({ mode: "create" })}><Plus size={17} /> New command</button>}>
    {composer && <CommandComposer key={composer.mode === "edit" ? composer.command.id : "new"} command={composer.mode === "edit" ? composer.command : undefined} busy={saving} onSave={saveCommand} onClose={() => setComposer(null)} />}
    {error && <div className="inline-error"><AlertTriangle size={16} /> {error}</div>}
    <FilterBar placeholder="Search commands" chips={["All", "Enabled", "Point-cost", "Moderator"]} />
    <div className="table-card"><table><thead><tr><th>Command</th><th>Response</th><th>Access</th><th>Cooldown</th><th>Cost</th><th>Uses</th><th>Status</th><th /></tr></thead><tbody>{commands.map((command) => <CommandRow key={command.id} command={command} busy={updatingId === command.id || deletingId === command.id} onToggle={() => void toggleCommand(command)} onEdit={() => setComposer({ mode: "edit", command })} onDelete={() => deleteCommand(command)} />)}{commands.length === 0 && <tr><td colSpan={8}><div className="table-empty">{loading ? <><RefreshCw className="spin" size={18} /> Loading commands…</> : "No commands yet. Create your first command above."}</div></td></tr>}</tbody></table></div>
    <section className="panel variable-panel"><SectionHeading eyebrow="Reusable text" title="Custom variables" action={<button className="secondary-button" onClick={() => setVariableComposer({ mode: "create" })}><Plus size={16} /> New variable</button>} />
      <p className="panel-description">Use a variable such as <code>{"{discord}"}</code> inside any command response. Built-ins: <code>{"{user}"}</code>, <code>{"{target}"}</code>, <code>{"{args}"}</code>, and <code>{"{snacks}"}</code>.</p>
      {variableComposer && <VariableComposer key={variableComposer.mode === "edit" ? variableComposer.variable.id : "new-variable"} variable={variableComposer.mode === "edit" ? variableComposer.variable : undefined} busy={saving} onSave={saveVariable} onClose={() => setVariableComposer(null)} />}
      <div className="table-card"><table><thead><tr><th>Variable</th><th>Value</th><th>Use in responses</th><th /></tr></thead><tbody>{variables.map((variable) => <VariableRow key={variable.id} variable={variable} busy={deletingId === variable.id} onEdit={() => setVariableComposer({ mode: "edit", variable })} onDelete={() => deleteVariable(variable)} />)}{variables.length === 0 && <tr><td colSpan={4}><div className="table-empty">{loading ? <><RefreshCw className="spin" size={18} /> Loading variables…</> : "No custom variables yet."}</div></td></tr>}</tbody></table></div>
    </section>
  </ModulePage>;
}

function CommandRow({ command, busy, onToggle, onEdit, onDelete }: { command: CommandRecord; busy: boolean; onToggle: () => void; onEdit: () => void; onDelete: () => Promise<void> }) {
  const [confirming, setConfirming] = useState(false);
  return <tr><td><div className="command-name"><strong>!{command.name}</strong>{command.aliases.length > 0 && <small>{command.aliases.map((alias) => `!${alias}`).join(", ")}</small>}</div></td><td className="response-cell">{command.response}</td><td><span className="soft-tag">{command.permission}</span></td><td>{command.cooldownSeconds}s</td><td>{command.cost ? `${command.cost.toLocaleString()} snacks` : "Free"}</td><td>{command.uses.toLocaleString()}</td><td><Toggle checked={command.enabled} disabled={busy} onChange={onToggle} /></td><td><div className="row-actions"><button className="icon-button" disabled={busy} title="Edit command" onClick={onEdit}><Pencil size={15} /></button><button className={`icon-button ${confirming ? "danger" : ""}`} disabled={busy} title={confirming ? "Click again to confirm deletion" : "Delete command"} onClick={() => confirming ? void onDelete() : setConfirming(true)}>{confirming ? <span>Sure?</span> : <Trash2 size={15} />}</button></div></td></tr>;
}

function VariableRow({ variable, busy, onEdit, onDelete }: { variable: VariableRecord; busy: boolean; onEdit: () => void; onDelete: () => Promise<void> }) {
  const [confirming, setConfirming] = useState(false);
  return <tr><td><strong>{`{${variable.name}}`}</strong></td><td className="response-cell">{variable.value}</td><td><code>{`{${variable.name}}`}</code></td><td><div className="row-actions"><button className="icon-button" disabled={busy} title="Edit variable" onClick={onEdit}><Pencil size={15} /></button><button className={`icon-button ${confirming ? "danger" : ""}`} disabled={busy} title={confirming ? "Click again to confirm deletion" : "Delete variable"} onClick={() => confirming ? void onDelete() : setConfirming(true)}>{confirming ? <span>Sure?</span> : <Trash2 size={15} />}</button></div></td></tr>;
}

function TimersPage({ preview }: { preview: boolean }) {
  const [timers, setTimers] = useState<TimerRecord[]>(preview ? demoTimers : []);
  const [composer, setComposer] = useState<{ mode: "create" } | { mode: "edit"; timer: TimerRecord } | null>(null);
  const [loading, setLoading] = useState(!preview);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (preview) return;
    setLoading(true);
    api<TimerRecord[]>("/api/timers")
      .then(setTimers)
      .catch((caught) => setError(caught instanceof Error ? caught.message : "Could not load timers."))
      .finally(() => setLoading(false));
  }, [preview]);

  async function saveTimer(draft: Omit<TimerRecord, "id" | "lastSentAt">) {
    const editing = composer?.mode === "edit" ? composer.timer : null;
    if (preview) {
      setTimers((items) => editing
        ? items.map((item) => item.id === editing.id ? { ...editing, ...draft } : item)
        : [...items, { id: crypto.randomUUID(), ...draft, lastSentAt: null }]);
      setComposer(null);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const saved = await api<TimerRecord>(editing ? `/api/timers/${editing.id}` : "/api/timers", { method: editing ? "PATCH" : "POST", body: JSON.stringify(draft) });
      setTimers((items) => editing ? items.map((item) => item.id === editing.id ? saved : item) : [...items, saved]);
      setComposer(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save the timer.");
    } finally {
      setSaving(false);
    }
  }

  async function toggleTimer(timer: TimerRecord) {
    if (preview) {
      setTimers((items) => items.map((item) => item.id === timer.id ? { ...item, enabled: !item.enabled } : item));
      return;
    }
    setBusyId(timer.id);
    setError("");
    try {
      const updated = await api<TimerRecord>(`/api/timers/${timer.id}`, { method: "PATCH", body: JSON.stringify({ enabled: !timer.enabled }) });
      setTimers((items) => items.map((item) => item.id === timer.id ? updated : item));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not update the timer.");
    } finally {
      setBusyId(null);
    }
  }

  async function deleteTimer(timer: TimerRecord) {
    if (preview) {
      setTimers((items) => items.filter((item) => item.id !== timer.id));
      return;
    }
    setBusyId(timer.id);
    setError("");
    try {
      await api<{ deleted: boolean }>(`/api/timers/${timer.id}`, { method: "DELETE" });
      setTimers((items) => items.filter((item) => item.id !== timer.id));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not delete the timer.");
    } finally {
      setBusyId(null);
    }
  }

  return <ModulePage eyebrow="Live messaging" title="Timers" description="Messages post only while live, after both the time interval and minimum chat activity are satisfied." action={<button className="primary-button" onClick={() => setComposer({ mode: "create" })}><Plus size={17} /> New timer</button>}>
    {composer && <TimerComposer key={composer.mode === "edit" ? composer.timer.id : "new-timer"} timer={composer.mode === "edit" ? composer.timer : undefined} busy={saving} onSave={saveTimer} onClose={() => setComposer(null)} />}
    {error && <div className="inline-error"><AlertTriangle size={16} /> {error}</div>}
    <div className="rule-callout"><Clock3 size={18} /><div><strong>Two-signal protection is on</strong><p>Quiet chat stays quiet. Every timer needs elapsed time and fresh chat activity.</p></div></div>
    <div className="card-grid two">{timers.map((timer) => <TimerCard key={timer.id} timer={timer} busy={busyId === timer.id} onToggle={() => toggleTimer(timer)} onEdit={() => setComposer({ mode: "edit", timer })} onDelete={() => deleteTimer(timer)} />)}{timers.length === 0 && <div className="panel timer-empty">{loading ? <><RefreshCw className="spin" size={18} /> Loading timers…</> : <>No timers yet. Create your first live message timer.</>}</div>}</div>
  </ModulePage>;
}

function TimerCard({ timer, busy, onToggle, onEdit, onDelete }: { timer: TimerRecord; busy: boolean; onToggle: () => Promise<void>; onEdit: () => void; onDelete: () => Promise<void> }) {
  const [confirming, setConfirming] = useState(false);
  return <article className="module-card"><div className="module-card-head"><span className="module-icon"><Clock3 /></span><Toggle checked={timer.enabled} disabled={busy} onChange={() => void onToggle()} /></div><h3>{timer.name}</h3><p>“{timer.message}”</p><div className="metric-row"><span><Clock3 size={15} /> Every {timer.intervalMinutes} min</span><span><MessageSquareText size={15} /> {timer.minimumChatMessages} messages</span></div><footer><span>Last posted {formatAgo(timer.lastSentAt)}</span><div className="timer-actions"><button className="text-button" disabled={busy} onClick={onEdit}><Pencil size={14} /> Edit</button><button className={`text-button ${confirming ? "danger-text" : ""}`} disabled={busy} onClick={() => confirming ? void onDelete() : setConfirming(true)}>{confirming ? "Confirm delete" : <><Trash2 size={14} /> Delete</>}</button></div></footer></article>;
}

function TimerComposer({ timer, busy, onSave, onClose }: { timer?: TimerRecord; busy: boolean; onSave: (draft: Omit<TimerRecord, "id" | "lastSentAt">) => Promise<void>; onClose: () => void }) {
  const [name, setName] = useState(timer?.name ?? "");
  const [message, setMessage] = useState(timer?.message ?? "");
  const [intervalMinutes, setIntervalMinutes] = useState(timer?.intervalMinutes ?? 20);
  const [minimumChatMessages, setMinimumChatMessages] = useState(timer?.minimumChatMessages ?? 5);

  async function submit(event: FormEvent) {
    event.preventDefault();
    await onSave({ name: name.trim(), message: message.trim(), enabled: timer?.enabled ?? true, intervalMinutes, minimumChatMessages });
  }

  return <div className="inline-composer timer-composer"><div><h3>{timer ? `Edit ${timer.name}` : "Create timer"}</h3><button className="icon-button" type="button" aria-label="Close timer form" onClick={onClose}><X size={18} /></button></div><form onSubmit={(event) => void submit(event)}><label>Timer name<input value={name} onChange={(event) => setName(event.target.value)} placeholder="Community reminder" maxLength={80} required /></label><label>Chat message<input value={message} onChange={(event) => setMessage(event.target.value)} placeholder="Remember to follow the channel!" maxLength={500} required /></label><label>Post every (minutes)<input type="number" value={intervalMinutes} onChange={(event) => setIntervalMinutes(Number(event.target.value))} min={1} max={1440} required /></label><label>Minimum chat messages<input type="number" value={minimumChatMessages} onChange={(event) => setMinimumChatMessages(Number(event.target.value))} min={0} max={10_000} required /></label><button className="primary-button" type="submit" disabled={busy}>{busy ? <RefreshCw className="spin" size={16} /> : <Save size={16} />} {busy ? "Saving…" : "Save timer"}</button></form></div>;
}

function ModerationPage({ preview }: { preview: boolean }) {
  const [rules, setRules] = useState<ModerationRuleRecord[]>(preview ? demoModerationRules : []);
  const [incidents, setIncidents] = useState<ModerationIncidentRecord[]>(preview ? demoModerationIncidents : []);
  const [loading, setLoading] = useState(!preview);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState("");

  async function refreshModeration() {
    if (preview) return;
    setLoading(true);
    setError("");
    try {
      const [nextRules, nextIncidents] = await Promise.all([
        api<ModerationRuleRecord[]>("/api/moderation/rules"),
        api<ModerationIncidentRecord[]>("/api/moderation/incidents"),
      ]);
      setRules(nextRules);
      setIncidents(nextIncidents);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load moderation data.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (preview) return;
    void refreshModeration();
  }, [preview]);

  async function updateRule(rule: ModerationRuleRecord, update: { enabled?: boolean; threshold?: number }) {
    if (preview) {
      setRules((items) => items.map((item) => item.id === rule.id ? { ...item, ...update } : item));
      return;
    }
    setBusyId(rule.id);
    setError("");
    try {
      const saved = await api<ModerationRuleRecord>(`/api/moderation/rules/${rule.ruleType}`, { method: "PATCH", body: JSON.stringify(update) });
      setRules((items) => items.map((item) => item.id === rule.id ? saved : item));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not update the moderation rule.");
    } finally {
      setBusyId(null);
    }
  }

  async function deleteIncident(incident: ModerationIncidentRecord) {
    if (preview) {
      setIncidents((items) => items.filter((item) => item.id !== incident.id));
      return;
    }
    setBusyId(incident.id);
    setError("");
    try {
      await api<{ deleted: boolean }>(`/api/moderation/incidents/${incident.id}`, { method: "DELETE" });
      setIncidents((items) => items.filter((item) => item.id !== incident.id));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not delete the incident.");
    } finally {
      setBusyId(null);
    }
  }

  return <ModulePage eyebrow="Safety desk" title="Moderation" description="The bot can delete, warn, and timeout. Permanent bans always wait for human approval." action={<button className="secondary-button" disabled={loading} onClick={() => void refreshModeration()}><RefreshCw className={loading ? "spin" : ""} size={17} /> Refresh</button>}>
    {error && <div className="inline-error"><AlertTriangle size={16} /> {error}</div>}
    <div className="safety-banner"><ShieldCheck size={24} /><div><strong>Human-in-the-loop bans</strong><p>Automatic enforcement stops at timeout. Kick moderators can use approved chat commands; dashboard access remains owner-only.</p></div><StatePill state="online" /></div>
    <div className="moderation-layout"><div className="panel"><SectionHeading eyebrow="Incident history" title="Recent decisions" /><div className="incident-list">{incidents.map((incident) => <Incident key={incident.id} incident={incident} busy={busyId === incident.id} onDelete={() => deleteIncident(incident)} />)}{incidents.length === 0 && <div className="moderation-empty">{loading ? <><RefreshCw className="spin" size={18} /> Loading incidents…</> : "No moderation incidents recorded."}</div>}</div></div><div className="moderation-side"><div className="panel"><SectionHeading eyebrow="Automatic rules" title="Detection controls" /><div className="moderation-rules">{rules.map((rule) => <ModerationRule key={rule.id} rule={rule} busy={busyId === rule.id} onUpdate={(update) => updateRule(rule, update)} />)}</div></div><div className="panel"><SectionHeading eyebrow="Automatic authority" title="Enforcement ladder" /><div className="ladder"><span>01</span><div><strong>Delete</strong><p>Remove the violating message.</p></div><span>02</span><div><strong>Warn</strong><p>Record and explain the rule.</p></div><span>03</span><div><strong>Timeout</strong><p>Escalate repeat violations for 10 minutes.</p></div><span className="human">04</span><div><strong>Human ban review</strong><p>Owner or Kick moderator decides.</p></div></div></div></div></div>
  </ModulePage>;
}

function ModerationRule({ rule, busy, onUpdate }: { rule: ModerationRuleRecord; busy: boolean; onUpdate: (update: { enabled?: boolean; threshold?: number }) => Promise<void> }) {
  const [threshold, setThreshold] = useState(rule.threshold);
  useEffect(() => setThreshold(rule.threshold), [rule.threshold]);
  const minimum = rule.ruleType === "character_spam" ? 4 : 1;
  return <div className="moderation-rule"><div><strong>{rule.label}</strong><p>{rule.description}</p></div><Toggle checked={rule.enabled} disabled={busy} onChange={() => void onUpdate({ enabled: !rule.enabled })} /><label>Trigger at<input type="number" value={threshold} min={minimum} max={50} onChange={(event) => setThreshold(Number(event.target.value))} /></label><button className="secondary-button" disabled={busy || threshold === rule.threshold || threshold < minimum || threshold > 50} onClick={() => void onUpdate({ threshold })}><Save size={14} /> Save</button></div>;
}

function Incident({ incident, busy, onDelete }: { incident: ModerationIncidentRecord; busy: boolean; onDelete: () => Promise<void> }) {
  const [confirming, setConfirming] = useState(false);
  const action = incident.action === "timeout" && incident.durationMinutes ? `Timed out · ${incident.durationMinutes}m` : incident.action === "warn" ? "Warning issued" : incident.action === "delete" ? "Message deleted" : incident.action.replace("_", " ");
  const rule = incident.ruleType.replaceAll("_", " ");
  return <div><span className="avatar">{incident.username[0]?.toUpperCase()}</span><div><strong>{incident.username}</strong><p>{action}</p><small>{rule} · “{incident.evidence}”</small></div><div className="incident-actions"><time>{formatAgo(incident.createdAt)}</time><button className={`text-button ${confirming ? "danger-text" : ""}`} disabled={busy} onClick={() => confirming ? void onDelete() : setConfirming(true)}>{confirming ? "Confirm" : <Trash2 size={14} />}</button></div></div>;
}

function ViewersPage({ preview }: { preview: boolean }) {
  const [viewers, setViewers] = useState<ViewerRecord[]>(preview ? demoViewers : []);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<"all" | "subscriber" | "moderator" | "new">("all");
  const [profile, setProfile] = useState<ViewerProfileRecord | null>(null);
  const [loading, setLoading] = useState(!preview);
  const [profileLoading, setProfileLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (preview) return;
    setLoading(true);
    api<ViewerRecord[]>("/api/viewers").then(setViewers).catch((caught) => setError(caught instanceof Error ? caught.message : "Could not load viewers.")).finally(() => setLoading(false));
  }, [preview]);

  const filtered = useMemo(() => viewers.filter((viewer) => {
    const matchesQuery = viewer.username.toLowerCase().includes(query.trim().toLowerCase()) || viewer.displayName.toLowerCase().includes(query.trim().toLowerCase());
    const isNew = Date.now() - new Date(viewer.firstSeenAt).getTime() < 7 * 86_400_000;
    const matchesFilter = filter === "all" || filter === "subscriber" && viewer.isSubscriber || filter === "moderator" && viewer.isModerator || filter === "new" && isNew;
    return matchesQuery && matchesFilter;
  }), [filter, query, viewers]);

  async function openProfile(viewer: ViewerRecord) {
    setProfileLoading(true);
    setError("");
    try {
      setProfile(preview ? demoViewerProfile(viewer) : await api<ViewerProfileRecord>(`/api/viewers/${viewer.id}`));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load that viewer profile.");
    } finally {
      setProfileLoading(false);
    }
  }

  const filters = [["all", "All viewers"], ["subscriber", "Subscribers"], ["moderator", "Moderators"], ["new", "New"]] as const;
  return <ModulePage eyebrow="Community memory" title="Viewer profiles" description="See verified chat activity, roles, SleepySnacks, moderation history, and event participation.">
    <div className="filter-bar"><label><ListFilter size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Find a viewer" /></label><div>{filters.map(([value, label]) => <button className={filter === value ? "active" : ""} key={value} onClick={() => setFilter(value)}>{label}</button>)}</div></div>
    {error && <div className="inline-error"><AlertTriangle size={16} /> {error}</div>}
    <div className="viewer-layout"><div className="table-card"><table><thead><tr><th>Viewer</th><th>Messages</th><th>Balance</th><th>Lifetime</th><th>Role</th><th>Last active</th><th /></tr></thead><tbody>{filtered.map((viewer) => <tr key={viewer.id}><td><div className="viewer-cell"><span className="avatar">{viewer.username[0]?.toUpperCase()}</span><div><strong>{viewer.displayName}</strong><small>@{viewer.username}</small></div></div></td><td>{viewer.messageCount.toLocaleString()}</td><td>{viewer.balance.toLocaleString()}</td><td>{viewer.lifetime.toLocaleString()}</td><td><span className="soft-tag">{viewer.role}</span></td><td>{formatAgo(viewer.lastSeenAt)}</td><td><button className="text-button" disabled={profileLoading} onClick={() => void openProfile(viewer)}>View</button></td></tr>)}</tbody></table>{filtered.length === 0 && <div className="table-empty">{loading ? <><RefreshCw className="spin" size={17} /> Loading viewers…</> : "No viewers match this search."}</div>}</div>
    {profile && <ViewerProfile profile={profile} onClose={() => setProfile(null)} />}</div>
  </ModulePage>;
}

function ViewerProfile({ profile, onClose }: { profile: ViewerProfileRecord; onClose: () => void }) {
  const { viewer } = profile;
  return <aside className="panel viewer-profile"><div className="profile-heading"><div className="viewer-cell"><span className="avatar">{viewer.username[0]?.toUpperCase()}</span><div><strong>{viewer.displayName}</strong><small>@{viewer.username}</small></div></div><button className="icon-button" aria-label="Close viewer profile" onClick={onClose}><X size={17} /></button></div><span className="soft-tag">{viewer.role}</span><div className="profile-metrics"><div><strong>{viewer.balance.toLocaleString()}</strong><small>Current snacks</small></div><div><strong>{viewer.lifetime.toLocaleString()}</strong><small>Lifetime earned</small></div><div><strong>{viewer.messageCount.toLocaleString()}</strong><small>Chat messages</small></div></div><div className="profile-section"><strong>Recent SleepySnacks activity</strong>{profile.recentTransactions.map((entry) => <div className="profile-event" key={entry.id}><span className={entry.amount >= 0 ? "positive" : "negative"}>{entry.amount >= 0 ? "+" : ""}{entry.amount.toLocaleString()}</span><p>{entry.reason}<small>{formatAgo(entry.createdAt)}</small></p></div>)}{profile.recentTransactions.length === 0 && <p className="profile-empty">No transactions yet.</p>}</div><div className="profile-section"><strong>Moderation history</strong>{profile.incidents.map((incident) => <div className="profile-event" key={incident.id}><ShieldCheck size={15} /><p>{incident.action.replace("_", " ")} · {incident.ruleType.replaceAll("_", " ")}<small>{formatAgo(incident.createdAt)}</small></p></div>)}{profile.incidents.length === 0 && <p className="profile-empty">No moderation incidents.</p>}</div></aside>;
}

function SnacksPage({ preview }: { preview: boolean }) {
  const [summary, setSummary] = useState<SnacksSummary>(preview ? demoSnacksSummary : { circulation: 0, lifetimeEarned: 0, viewers: 0, transactionsToday: 0 });
  const [leaders, setLeaders] = useState<SnackLeaderboardRow[]>(preview ? demoLeaders : []);
  const [ledger, setLedger] = useState<SnackLedgerRecord[]>(preview ? demoSnackLedger : []);
  const [viewers, setViewers] = useState<ViewerRecord[]>(preview ? demoViewers : []);
  const [composerOpen, setComposerOpen] = useState(false);
  const [viewerId, setViewerId] = useState(preview ? demoViewers[0]?.id ?? "" : "");
  const [amount, setAmount] = useState(25);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(!preview);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function refresh() {
    if (preview) return;
    setLoading(true);
    setError("");
    try {
      const [nextSummary, nextLeaders, nextLedger, nextViewers] = await Promise.all([api<SnacksSummary>("/api/snacks/summary"), api<SnackLeaderboardRow[]>("/api/snacks/leaderboard"), api<SnackLedgerRecord[]>("/api/snacks/ledger"), api<ViewerRecord[]>("/api/viewers")]);
      setSummary(nextSummary); setLeaders(nextLeaders); setLedger(nextLedger); setViewers(nextViewers); setViewerId((current) => current || nextViewers[0]?.id || "");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load SleepySnacks.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void refresh(); }, [preview]);

  async function adjust(event: FormEvent) {
    event.preventDefault();
    setBusy(true); setError("");
    try {
      if (preview) {
        const viewer = viewers.find((entry) => entry.id === viewerId);
        if (viewer) setLedger((current) => [{ id: crypto.randomUUID(), viewerId, username: viewer.username, amount, reason: `Owner adjustment: ${reason}`, createdAt: new Date().toISOString() }, ...current]);
      } else {
        await api<{ adjusted: boolean }>("/api/snacks/adjust", { method: "POST", body: JSON.stringify({ viewerId, amount, reason }) });
        await refresh();
      }
      setComposerOpen(false); setReason("");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not adjust this balance.");
    } finally {
      setBusy(false);
    }
  }

  return <ModulePage eyebrow="Loyalty economy" title="SleepySnacks" description="Verified chat activity plus event bonuses, protected by anti-farm cooldowns and a complete transaction ledger." action={<button className="primary-button" onClick={() => setComposerOpen((open) => !open)}><Coins size={17} /> Adjust balance</button>}>
    {composerOpen && <div className="inline-composer snack-composer"><div><h3>Owner balance adjustment</h3><button className="icon-button" aria-label="Close balance adjustment" onClick={() => setComposerOpen(false)}><X size={17} /></button></div><form onSubmit={(event) => void adjust(event)}><label>Viewer<select value={viewerId} onChange={(event) => setViewerId(event.target.value)} required><option value="">Choose a viewer</option>{viewers.map((viewer) => <option value={viewer.id} key={viewer.id}>@{viewer.username} · {viewer.balance.toLocaleString()}</option>)}</select></label><label>Amount<input type="number" min={-1_000_000} max={1_000_000} value={amount} onChange={(event) => setAmount(Number(event.target.value))} required /><small>Use a negative number to remove snacks.</small></label><label>Reason<input value={reason} onChange={(event) => setReason(event.target.value)} minLength={2} maxLength={120} placeholder="Contest prize or correction" required /></label><button className="primary-button" disabled={busy || !viewerId || amount === 0}>{busy ? <RefreshCw className="spin" size={16} /> : <Save size={16} />} Save adjustment</button></form></div>}
    {error && <div className="inline-error"><AlertTriangle size={16} /> {error}</div>}
    <div className="stats-row snack-stats"><MiniStat label="Snacks in circulation" value={summary.circulation.toLocaleString()} change="Current balances" /><MiniStat label="Lifetime earned" value={summary.lifetimeEarned.toLocaleString()} change="All verified awards" /><MiniStat label="Tracked viewers" value={summary.viewers.toLocaleString()} change="Connected Kick channel" /><MiniStat label="Transactions today" value={summary.transactionsToday.toLocaleString()} change="Awards and spending" /></div>
    <div className="snacks-layout"><div className="panel"><SectionHeading eyebrow="Top community" title="Leaderboard" /><div className="leaderboard">{leaders.slice(0, 10).map((leader) => <div key={leader.viewerId}><span className="rank-medal">{leader.rank}</span><span className="avatar">{leader.username[0]?.toUpperCase()}</span><strong>{leader.username}</strong><div><b>{leader.balance.toLocaleString()}</b><small>SleepySnacks</small></div></div>)}{leaders.length === 0 && <div className="table-empty">{loading ? "Loading leaderboard…" : "No balances yet."}</div>}</div></div><div className="panel"><SectionHeading eyebrow="Earning rules" title="Live economy" /><EconomyRule title="Verified chat activity" detail="25 snacks per eligible one-minute chat window" /><EconomyRule title="Follow bonus" detail="75 snacks once per verified follow" /><EconomyRule title="Subscription bonus" detail="500 snacks per verified subscription" /><EconomyRule title="Gifted sub bonus" detail="250 snacks per gifted subscription" /></div></div>
    <div className="panel ledger-panel"><SectionHeading eyebrow="Audit trail" title="Transaction history" /><div className="table-card"><table><thead><tr><th>Viewer</th><th>Amount</th><th>Reason</th><th>When</th></tr></thead><tbody>{ledger.map((entry) => <tr key={entry.id}><td><strong>@{entry.username}</strong></td><td className={entry.amount >= 0 ? "positive" : "negative"}>{entry.amount >= 0 ? "+" : ""}{entry.amount.toLocaleString()}</td><td>{entry.reason}</td><td>{formatAgo(entry.createdAt)}</td></tr>)}</tbody></table>{ledger.length === 0 && <div className="table-empty">{loading ? "Loading transactions…" : "No SleepySnacks transactions yet."}</div>}</div></div>
  </ModulePage>;
}

function EconomyRule({ title, detail }: { title: string; detail: string }) {
  return <div className="setting-row"><div><strong>{title}</strong><p>{detail}</p></div><StatePill state="online" /></div>;
}

type RewardDraft = Pick<RewardRecord, "name" | "description" | "chatCommand" | "cost" | "handlingMode" | "flowId" | "enabled">;

function RewardsPage({ preview }: { preview: boolean }) {
  const [rewards, setRewards] = useState<RewardRecord[]>(preview ? demoRewards : []);
  const [redemptions, setRedemptions] = useState<RedemptionRecord[]>(preview ? demoRedemptions : []);
  const [flows, setFlows] = useState<FlowRecord[]>(preview ? demoFlows : []);
  const [composerOpen, setComposerOpen] = useState(false);
  const [editing, setEditing] = useState<RewardRecord | undefined>();
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(!preview);
  const [error, setError] = useState("");

  async function refresh() {
    if (preview) return;
    setLoading(true); setError("");
    try {
      const [nextRewards, nextRedemptions, nextFlows] = await Promise.all([api<RewardRecord[]>("/api/rewards"), api<RedemptionRecord[]>("/api/redemptions"), api<FlowRecord[]>("/api/flows")]);
      setRewards(nextRewards); setRedemptions(nextRedemptions); setFlows(nextFlows);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load rewards.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void refresh(); }, [preview]);

  async function saveReward(draft: RewardDraft) {
    setBusyId(editing?.id ?? "new"); setError("");
    try {
      if (preview) {
        const flowName = flows.find((flow) => flow.id === draft.flowId)?.name ?? null;
        setRewards((current) => editing ? current.map((reward) => reward.id === editing.id ? { ...reward, ...draft, flowName } : reward) : [...current, { id: crypto.randomUUID(), ...draft, flowName, createdAt: new Date().toISOString() }]);
      } else {
        await api<RewardRecord>(editing ? `/api/rewards/${editing.id}` : "/api/rewards", { method: editing ? "PATCH" : "POST", body: JSON.stringify(draft) });
        await refresh();
      }
      setComposerOpen(false); setEditing(undefined);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save this reward.");
    } finally {
      setBusyId(null);
    }
  }

  async function toggleReward(reward: RewardRecord) {
    setBusyId(reward.id); setError("");
    try {
      if (preview) setRewards((current) => current.map((entry) => entry.id === reward.id ? { ...entry, enabled: !entry.enabled } : entry));
      else { await api<RewardRecord>(`/api/rewards/${reward.id}`, { method: "PATCH", body: JSON.stringify({ enabled: !reward.enabled }) }); await refresh(); }
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not update this reward."); }
    finally { setBusyId(null); }
  }

  async function deleteReward(reward: RewardRecord) {
    if (confirmingId !== reward.id) { setConfirmingId(reward.id); return; }
    setBusyId(reward.id); setError("");
    try {
      if (preview) setRewards((current) => current.filter((entry) => entry.id !== reward.id));
      else { await api<{ deleted: boolean }>(`/api/rewards/${reward.id}`, { method: "DELETE" }); await refresh(); }
      setConfirmingId(null);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not delete this reward."); }
    finally { setBusyId(null); }
  }

  async function resolveRedemption(redemption: RedemptionRecord, decision: "fulfill" | "reject") {
    setBusyId(redemption.id); setError("");
    try {
      if (preview) setRedemptions((current) => current.map((entry) => entry.id === redemption.id ? { ...entry, status: decision === "fulfill" ? "fulfilled" : "rejected", resolvedAt: new Date().toISOString() } : entry));
      else { await api<{ resolved: boolean }>(`/api/redemptions/${redemption.id}`, { method: "PATCH", body: JSON.stringify({ decision }) }); await refresh(); }
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not resolve this redemption."); }
    finally { setBusyId(null); }
  }

  const pending = redemptions.filter((redemption) => redemption.status === "pending").length;
  return <ModulePage eyebrow="Community rewards" title="Redemptions" description="Each reward can run instantly, join an approval queue, or launch a SleepyFlow." action={<button className="primary-button" onClick={() => { setEditing(undefined); setComposerOpen(true); }}><Plus size={17} /> New reward</button>}>
    {composerOpen && <RewardComposer reward={editing} flows={flows} busy={busyId === (editing?.id ?? "new")} onSave={saveReward} onClose={() => { setComposerOpen(false); setEditing(undefined); }} />}
    {error && <div className="inline-error"><AlertTriangle size={16} /> {error}</div>}
    <div className="reward-summary"><span><Gift size={17} /><strong>{rewards.length}</strong> rewards</span><span><Clock3 size={17} /><strong>{pending}</strong> awaiting owner</span><span><Coins size={17} /><strong>!redeem</strong> chat entry</span></div>
    <div className="card-grid three">{rewards.map((reward) => <article className={`reward-card ${reward.enabled ? "" : "disabled"}`} key={reward.id}><span className="reward-symbol"><Gift /></span><div><small>{reward.handlingMode === "sleepyflow" ? "SleepyFlow" : reward.handlingMode}</small><h3>{reward.name}</h3><p><Coins size={15} /> {reward.cost.toLocaleString()} SleepySnacks</p><code>!redeem {reward.chatCommand}</code>{reward.description && <p className="reward-description">{reward.description}</p>}</div><div className="reward-actions"><Toggle checked={reward.enabled} disabled={busyId === reward.id} onChange={() => void toggleReward(reward)} /><button className="icon-button" aria-label={`Edit ${reward.name}`} onClick={() => { setEditing(reward); setComposerOpen(true); }}><Pencil size={15} /></button><button className={`icon-button ${confirmingId === reward.id ? "danger-button" : ""}`} aria-label={`Delete ${reward.name}`} disabled={busyId === reward.id} onClick={() => void deleteReward(reward)}>{confirmingId === reward.id ? <span>Confirm</span> : <Trash2 size={15} />}</button></div></article>)}{rewards.length === 0 && <div className="panel reward-empty">{loading ? <><RefreshCw className="spin" /> Loading rewards…</> : <><Gift /><strong>No rewards yet</strong><p>Create the first SleepySnacks reward above.</p></>}</div>}</div>
    <div className="panel queue-panel"><SectionHeading eyebrow="Needs attention" title="Redemption queue" /><div className="redemption-list">{redemptions.map((redemption) => <div key={redemption.id}><span className="avatar">{redemption.username[0]?.toUpperCase()}</span><div><strong>{redemption.rewardName}</strong><p>@{redemption.username} · {redemption.cost.toLocaleString()} SleepySnacks</p>{redemption.userInput && <small>“{redemption.userInput}”</small>}</div><div className="redemption-status"><span className={`soft-tag ${redemption.status}`}>{redemption.status}</span><time>{formatAgo(redemption.createdAt)}</time></div>{redemption.status === "pending" && <div className="redemption-actions"><button className="secondary-button" disabled={busyId === redemption.id} onClick={() => void resolveRedemption(redemption, "fulfill")}><ShieldCheck size={14} /> Fulfill</button><button className="text-button danger-text" disabled={busyId === redemption.id} onClick={() => void resolveRedemption(redemption, "reject")}>Reject & refund</button></div>}</div>)}{redemptions.length === 0 && <EmptyState icon={<Gift />} title="No redemptions yet" text="Viewers can enter !redeem followed by a reward command while the channel is live." />}</div></div>
  </ModulePage>;
}

function RewardComposer({ reward, flows, busy, onSave, onClose }: { reward?: RewardRecord; flows: FlowRecord[]; busy: boolean; onSave: (draft: RewardDraft) => Promise<void>; onClose: () => void }) {
  const [name, setName] = useState(reward?.name ?? "");
  const [description, setDescription] = useState(reward?.description ?? "");
  const [chatCommand, setChatCommand] = useState(reward?.chatCommand ?? "");
  const [cost, setCost] = useState(reward?.cost ?? 100);
  const [handlingMode, setHandlingMode] = useState<RewardHandlingMode>(reward?.handlingMode ?? "instant");
  const [flowId, setFlowId] = useState(reward?.flowId ?? "");

  async function submit(event: FormEvent) {
    event.preventDefault();
    await onSave({ name: name.trim(), description: description.trim(), chatCommand: chatCommand.trim().replace(/^!?(?:redeem\s+)?/i, "").toLowerCase(), cost, handlingMode, flowId: handlingMode === "sleepyflow" ? flowId || null : null, enabled: reward?.enabled ?? true });
  }

  return <div className="inline-composer reward-composer"><div><h3>{reward ? `Edit ${reward.name}` : "Create reward"}</h3><button className="icon-button" aria-label="Close reward form" onClick={onClose}><X size={17} /></button></div><form onSubmit={(event) => void submit(event)}><label>Reward name<input value={name} onChange={(event) => setName(event.target.value)} maxLength={80} placeholder="Hydration check" required /></label><label>Chat command<div className="command-input"><span>!redeem</span><input value={chatCommand} onChange={(event) => setChatCommand(event.target.value)} pattern="[A-Za-z0-9_]{1,32}" maxLength={32} placeholder="hydrate" required /></div></label><label>SleepySnacks cost<input type="number" value={cost} onChange={(event) => setCost(Number(event.target.value))} min={1} max={1_000_000_000} required /></label><label>Handling<select value={handlingMode} onChange={(event) => setHandlingMode(event.target.value as RewardHandlingMode)}><option value="instant">Instant</option><option value="queued">Owner queue</option><option value="sleepyflow">SleepyFlow</option></select></label>{handlingMode === "sleepyflow" && <label>SleepyFlow<select value={flowId} onChange={(event) => setFlowId(event.target.value)} required><option value="">Choose a flow</option>{flows.map((flow) => <option value={flow.id} key={flow.id} disabled={!flow.enabled}>{flow.name}{flow.enabled ? "" : " (disabled)"}</option>)}</select></label>}<label className="reward-description-field">Description<input value={description} onChange={(event) => setDescription(event.target.value)} maxLength={300} placeholder="What happens when this is redeemed?" /></label><button className="primary-button" disabled={busy || handlingMode === "sleepyflow" && !flowId}>{busy ? <RefreshCw className="spin" size={16} /> : <Save size={16} />} Save reward</button></form></div>;
}

type GiveawayDraft = Pick<GiveawayRecord, "title" | "prize" | "entryCommand">;

function GiveawaysPage({ preview }: { preview: boolean }) {
  const [giveaways, setGiveaways] = useState<GiveawayRecord[]>(preview ? demoGiveaways : []);
  const [composerOpen, setComposerOpen] = useState(false);
  const [editing, setEditing] = useState<GiveawayRecord | undefined>();
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(!preview);
  const [error, setError] = useState("");

  async function refresh() {
    if (preview) return;
    setLoading(true); setError("");
    try { setGiveaways(await api<GiveawayRecord[]>("/api/giveaways")); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "Could not load giveaways."); }
    finally { setLoading(false); }
  }

  useEffect(() => { void refresh(); }, [preview]);

  async function saveGiveaway(draft: GiveawayDraft) {
    setBusyId(editing?.id ?? "new"); setError("");
    try {
      if (preview) {
        setGiveaways((current) => editing
          ? current.map((giveaway) => giveaway.id === editing.id ? { ...giveaway, ...draft } : giveaway)
          : [{ id: crypto.randomUUID(), ...draft, status: "draft", entryCount: 0, drawCount: 0, winnerViewerId: null, winnerUsername: null, lastDrawAt: null, openedAt: null, closedAt: null, createdAt: new Date().toISOString() }, ...current]);
      } else {
        await api<GiveawayRecord>(editing ? `/api/giveaways/${editing.id}` : "/api/giveaways", { method: editing ? "PATCH" : "POST", body: JSON.stringify(draft) });
        await refresh();
      }
      setComposerOpen(false); setEditing(undefined);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not save this giveaway."); }
    finally { setBusyId(null); }
  }

  async function runAction(giveaway: GiveawayRecord, action: "open" | "close" | "draw" | "reroll") {
    setBusyId(giveaway.id); setError("");
    try {
      if (preview) {
        setGiveaways((current) => current.map((entry) => {
          if (entry.id !== giveaway.id) return entry;
          if (action === "open") return { ...entry, status: "open", openedAt: new Date().toISOString() };
          if (action === "close") return { ...entry, status: "closed", closedAt: new Date().toISOString() };
          const winner = demoViewers.find((viewer) => action !== "reroll" || viewer.id !== entry.winnerViewerId) ?? demoViewers[0];
          return { ...entry, status: "completed", drawCount: entry.drawCount + 1, winnerViewerId: winner?.id ?? null, winnerUsername: winner?.username ?? "PreviewWinner", lastDrawAt: new Date().toISOString(), closedAt: entry.closedAt ?? new Date().toISOString() };
        }));
      } else {
        await api<GiveawayRecord>(`/api/giveaways/${giveaway.id}/${action}`, { method: "POST", body: "{}" });
        await refresh();
      }
    } catch (caught) { setError(caught instanceof Error ? caught.message : `Could not ${action} this giveaway.`); }
    finally { setBusyId(null); }
  }

  async function deleteGiveaway(giveaway: GiveawayRecord) {
    if (confirmingId !== giveaway.id) { setConfirmingId(giveaway.id); return; }
    setBusyId(giveaway.id); setError("");
    try {
      if (preview) setGiveaways((current) => current.filter((entry) => entry.id !== giveaway.id));
      else { await api<{ deleted: true }>(`/api/giveaways/${giveaway.id}`, { method: "DELETE", body: "{}" }); await refresh(); }
      setConfirmingId(null);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not delete this giveaway."); }
    finally { setBusyId(null); }
  }

  const featured = giveaways.find((giveaway) => giveaway.status === "open") ?? giveaways.find((giveaway) => giveaway.status === "draft") ?? giveaways.find((giveaway) => giveaway.status === "closed");
  const completed = giveaways.filter((giveaway) => giveaway.status === "completed");

  return <ModulePage eyebrow="Live giveaways" title="Giveaways" description="Create a draw, open one chat entry command while live, and keep a verified winner history." action={<button className="primary-button" onClick={() => { setEditing(undefined); setComposerOpen(true); }}><Plus size={17} /> Create giveaway</button>}>
    {composerOpen && <GiveawayComposer giveaway={editing} busy={busyId === (editing?.id ?? "new")} onSave={saveGiveaway} onClose={() => { setComposerOpen(false); setEditing(undefined); }} />}
    {error && <div className="inline-error"><AlertTriangle size={16} /> {error}</div>}
    <div className={`giveaway-hero ${featured?.status ?? "empty"}`}><div><span className={`soft-tag ${featured?.status ?? ""}`}>{featured ? featured.status : "Ready to create"}</span><h2>{featured?.title ?? "Chat command giveaway"}</h2><p>{featured?.prize ?? "Create the first giveaway, choose its prize, and open entries when your Kick channel is live."}</p><div className="metric-row"><span><Command size={16} /> !{featured?.entryCommand ?? "enter"}</span><span><ShieldCheck size={16} /> One entry per viewer</span><span><UsersRound size={16} /> {featured?.entryCount.toLocaleString() ?? "0"} entries</span></div>{featured && <GiveawayActions giveaway={featured} busy={busyId === featured.id} confirming={confirmingId === featured.id} onAction={runAction} onEdit={() => { setEditing(featured); setComposerOpen(true); }} onDelete={() => void deleteGiveaway(featured)} />}</div><div className="giveaway-orb"><Trophy /></div></div>
    <div className="panel giveaway-library"><SectionHeading eyebrow="Control room" title="Giveaway library" /><div className="giveaway-list">{giveaways.map((giveaway) => <div key={giveaway.id}><span className="flow-symbol"><Trophy /></span><div><strong>{giveaway.title}</strong><p>{giveaway.prize}</p><small>!{giveaway.entryCommand} · {giveaway.entryCount.toLocaleString()} entries{giveaway.winnerUsername ? ` · winner @${giveaway.winnerUsername}` : ""}{giveaway.drawCount > 1 ? ` · ${giveaway.drawCount - 1} reroll${giveaway.drawCount === 2 ? "" : "s"}` : ""}</small></div><div className="giveaway-status"><span className={`soft-tag ${giveaway.status}`}>{giveaway.status}</span><time>{formatAgo(giveaway.lastDrawAt ?? giveaway.closedAt ?? giveaway.openedAt ?? giveaway.createdAt)}</time></div><GiveawayActions giveaway={giveaway} busy={busyId === giveaway.id} confirming={confirmingId === giveaway.id} compact onAction={runAction} onEdit={() => { setEditing(giveaway); setComposerOpen(true); }} onDelete={() => void deleteGiveaway(giveaway)} /></div>)}{giveaways.length === 0 && <EmptyState icon={<Trophy />} title={loading ? "Loading giveaways…" : "No giveaways yet"} text="Create a draft now and open entries when you go live." />}</div></div>
    <div className="panel"><SectionHeading eyebrow="Audit trail" title="Completed draws" />{completed.length === 0 ? <EmptyState icon={<Trophy />} title="No completed giveaways yet" text="Winner, entry count, and draw time will appear here." /> : <div className="winner-grid">{completed.map((giveaway) => <article key={giveaway.id}><Trophy /><small>Winner</small><strong>@{giveaway.winnerUsername}</strong><p>{giveaway.title} · {giveaway.prize}</p><span>{giveaway.entryCount.toLocaleString()} verified entries · {giveaway.drawCount.toLocaleString()} draw{giveaway.drawCount === 1 ? "" : "s"} · {formatAgo(giveaway.lastDrawAt)}</span><button className="secondary-button" disabled={busyId === giveaway.id || giveaway.entryCount < 2} onClick={() => void runAction(giveaway, "reroll")}><RefreshCw size={14} /> Reroll winner</button></article>)}</div>}</div>
  </ModulePage>;
}

function GiveawayComposer({ giveaway, busy, onSave, onClose }: { giveaway?: GiveawayRecord; busy: boolean; onSave: (draft: GiveawayDraft) => Promise<void>; onClose: () => void }) {
  const [title, setTitle] = useState(giveaway?.title ?? "");
  const [prize, setPrize] = useState(giveaway?.prize ?? "");
  const [entryCommand, setEntryCommand] = useState(giveaway?.entryCommand ?? "enter");
  async function submit(event: FormEvent) { event.preventDefault(); await onSave({ title: title.trim(), prize: prize.trim(), entryCommand: entryCommand.trim().replace(/^!/, "").toLowerCase() }); }
  return <div className="inline-composer giveaway-composer"><div><h3>{giveaway ? `Edit ${giveaway.title}` : "Create giveaway"}</h3><button className="icon-button" aria-label="Close giveaway form" onClick={onClose}><X size={17} /></button></div><form onSubmit={(event) => void submit(event)}><label>Giveaway title<input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={100} placeholder="Community game key" required /></label><label>Prize<input value={prize} onChange={(event) => setPrize(event.target.value)} maxLength={200} placeholder="One Steam game key" required /></label><label>Chat entry command<div className="command-input"><span>!</span><input value={entryCommand} onChange={(event) => setEntryCommand(event.target.value)} pattern="[A-Za-z0-9_]{1,32}" maxLength={32} placeholder="enter" required /></div></label><button className="primary-button" disabled={busy}>{busy ? <RefreshCw className="spin" size={16} /> : <Save size={16} />} Save draft</button></form></div>;
}

function GiveawayActions({ giveaway, busy, confirming, compact = false, onAction, onEdit, onDelete }: { giveaway: GiveawayRecord; busy: boolean; confirming: boolean; compact?: boolean; onAction: (giveaway: GiveawayRecord, action: "open" | "close" | "draw" | "reroll") => Promise<void>; onEdit: () => void; onDelete: () => void }) {
  return <div className={`giveaway-actions ${compact ? "compact" : ""}`}>{giveaway.status === "draft" && <><button className="primary-button" disabled={busy} onClick={() => void onAction(giveaway, "open")}><Play size={15} /> Open entries</button><button className="secondary-button" disabled={busy} onClick={onEdit}><Pencil size={14} /> Edit</button><button className={confirming ? "secondary-button danger-button" : "text-button danger-text"} disabled={busy} onClick={onDelete}>{confirming ? "Confirm delete" : <><Trash2 size={14} /> Delete</>}</button></>}{giveaway.status === "open" && <><button className="secondary-button" disabled={busy} onClick={() => void onAction(giveaway, "close")}><X size={14} /> Close entries</button><button className="primary-button" disabled={busy || giveaway.entryCount < 1} onClick={() => void onAction(giveaway, "draw")}><Trophy size={15} /> Draw winner</button></>}{giveaway.status === "closed" && <button className="primary-button" disabled={busy || giveaway.entryCount < 1} onClick={() => void onAction(giveaway, "draw")}><Trophy size={15} /> Draw winner</button>}</div>;
}

type FlowDraft = Pick<FlowRecord, "name" | "enabled" | "trigger" | "conditions" | "actions">;

const flowTriggerOptions: { value: FlowTrigger["type"]; label: string; detail: string }[] = [
  { value: "channel.followed", label: "New follower", detail: "A verified viewer follows the channel" },
  { value: "channel.subscription.new", label: "New subscription", detail: "A new subscription is verified" },
  { value: "channel.subscription.renewal", label: "Subscription renewal", detail: "A subscription renewal is verified" },
  { value: "channel.subscription.gifts", label: "Gifted subscriptions", detail: "One or more subscriptions are gifted" },
  { value: "kicks.gifted", label: "KICKs gifted", detail: "A verified KICKs gift arrives" },
  { value: "reward.redeemed", label: "Reward redeemed", detail: "A connected SleepySnacks reward runs this flow" },
];

const amountFlowTriggers = new Set<FlowTrigger["type"]>(["channel.subscription.gifts", "kicks.gifted"]);

function SleepyFlowPage({ preview }: { preview: boolean }) {
  const [flows, setFlows] = useState<FlowRecord[]>(preview ? demoFlows : []);
  const [composerOpen, setComposerOpen] = useState(false);
  const [editing, setEditing] = useState<FlowRecord | undefined>();
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(!preview);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  async function refresh() {
    if (preview) return;
    setLoading(true); setError("");
    try { setFlows(await api<FlowRecord[]>("/api/flows")); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "Could not load SleepyFlow."); }
    finally { setLoading(false); }
  }

  useEffect(() => { void refresh(); }, [preview]);

  async function saveFlow(draft: FlowDraft) {
    setBusyId(editing?.id ?? "new"); setError(""); setNotice("");
    try {
      if (preview) {
        const triggerType = flowTriggerOptions.find((option) => option.value === draft.trigger.type)?.label ?? "Custom trigger";
        setFlows((current) => editing
          ? current.map((flow) => flow.id === editing.id ? { ...flow, ...draft, triggerType, updatedAt: new Date().toISOString() } : flow)
          : [{ id: crypto.randomUUID(), ...draft, triggerType, runs: 0, lastRunAt: null, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }, ...current]);
      } else {
        await api<FlowRecord>(editing ? `/api/flows/${editing.id}` : "/api/flows", { method: editing ? "PATCH" : "POST", body: JSON.stringify(draft) });
        await refresh();
      }
      setComposerOpen(false); setEditing(undefined); setNotice("SleepyFlow saved.");
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not save this SleepyFlow."); }
    finally { setBusyId(null); }
  }

  async function toggleFlow(flow: FlowRecord) {
    setBusyId(flow.id); setError(""); setNotice("");
    try {
      if (preview) setFlows((current) => current.map((entry) => entry.id === flow.id ? { ...entry, enabled: !entry.enabled } : entry));
      else { await api<FlowRecord>(`/api/flows/${flow.id}`, { method: "PATCH", body: JSON.stringify({ enabled: !flow.enabled }) }); await refresh(); }
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not update this SleepyFlow."); }
    finally { setBusyId(null); }
  }

  async function testFlow(flow: FlowRecord) {
    setBusyId(flow.id); setError(""); setNotice("");
    try {
      const result = preview
        ? { previews: flow.actions.map((action) => action.type), alertsPreviewed: flow.actions.filter((action) => action.type === "show_alert").length, safe: true }
        : await api<{ previews: string[]; alertsPreviewed: number; safe: boolean }>(`/api/flows/${flow.id}/test`, { method: "POST", body: "{}" });
      setNotice(`Test passed: ${result.previews.length} action${result.previews.length === 1 ? "" : "s"} validated${result.alertsPreviewed > 0 ? ` and ${result.alertsPreviewed} alert preview${result.alertsPreviewed === 1 ? " was" : "s were"} sent` : ""}. No chat messages or SleepySnacks were sent.`);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not test this SleepyFlow."); }
    finally { setBusyId(null); }
  }

  async function deleteFlow(flow: FlowRecord) {
    if (confirmingId !== flow.id) { setConfirmingId(flow.id); return; }
    setBusyId(flow.id); setError(""); setNotice("");
    try {
      if (preview) setFlows((current) => current.filter((entry) => entry.id !== flow.id));
      else { await api<{ deleted: true }>(`/api/flows/${flow.id}`, { method: "DELETE", body: "{}" }); await refresh(); }
      setConfirmingId(null);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not delete this SleepyFlow."); }
    finally { setBusyId(null); }
  }

  const featured = flows[0];
  const featuredTrigger = flowTriggerOptions.find((option) => option.value === featured?.trigger.type);
  return <ModulePage eyebrow="Visual automation" title="SleepyFlow" description="Choose what starts the automation, add an optional threshold, and stack up to five actions—without writing code." action={<button className="primary-button" onClick={() => { setEditing(undefined); setComposerOpen(true); }}><Plus size={17} /> New flow</button>}>
    {composerOpen && <FlowComposer flow={editing} busy={busyId === (editing?.id ?? "new")} onSave={saveFlow} onClose={() => { setComposerOpen(false); setEditing(undefined); }} />}
    {error && <div className="inline-error"><AlertTriangle size={16} /> {error}</div>}
    {notice && <div className="inline-success"><ShieldCheck size={16} /> {notice}</div>}
    <div className="flow-starter"><div className="flow-node trigger"><small>WHEN</small><strong><Radio size={18} /> {featuredTrigger?.label ?? "A verified Kick event"}</strong><p>{featuredTrigger?.detail ?? "Create a flow to choose its trigger"}</p></div><span className="flow-line"><Zap size={16} /></span><div className="flow-node condition"><small>IF</small><strong><ListFilter size={18} /> {featured?.conditions.length ? `Minimum ${featured.conditions[0]?.value.toLocaleString()}` : "No extra threshold"}</strong><p>Every automation remains live-only</p></div><span className="flow-line"><Zap size={16} /></span><div className="flow-node action"><small>THEN</small><strong><Sparkles size={18} /> {featured?.actions.length ?? 0} action{featured?.actions.length === 1 ? "" : "s"}</strong><p>Chat · alert · SleepySnacks</p></div></div>
    <div className="panel"><SectionHeading eyebrow="Automation library" title="Your flows" /><div className="flow-list">{flows.map((flow) => <div className={flow.enabled ? "" : "disabled"} key={flow.id}><span className="flow-symbol"><WandSparkles /></span><div><strong>{flow.name}</strong><p>{flow.triggerType} · {flow.conditions.length} condition{flow.conditions.length === 1 ? "" : "s"} · {flow.actions.length} action{flow.actions.length === 1 ? "" : "s"}</p></div><span>{flow.runs.toLocaleString()} runs<small>{formatAgo(flow.lastRunAt)}</small></span><Toggle checked={flow.enabled} disabled={busyId === flow.id} onChange={() => void toggleFlow(flow)} /><div className="flow-actions"><button className="secondary-button" disabled={busyId === flow.id} onClick={() => void testFlow(flow)}><Play size={14} /> Test</button><button className="icon-button" aria-label={`Edit ${flow.name}`} onClick={() => { setEditing(flow); setComposerOpen(true); }}><Pencil size={15} /></button><button className={`icon-button ${confirmingId === flow.id ? "danger" : ""}`} aria-label={`Delete ${flow.name}`} disabled={busyId === flow.id} onClick={() => void deleteFlow(flow)}>{confirmingId === flow.id ? "Confirm" : <Trash2 size={15} />}</button></div></div>)}{flows.length === 0 && <EmptyState icon={<WandSparkles />} title={loading ? "Loading SleepyFlow…" : "No automations yet"} text="Create the first flow and safely test it before enabling it." />}</div></div>
  </ModulePage>;
}

function FlowComposer({ flow, busy, onSave, onClose }: { flow?: FlowRecord; busy: boolean; onSave: (draft: FlowDraft) => Promise<void>; onClose: () => void }) {
  const [name, setName] = useState(flow?.name ?? "");
  const [triggerType, setTriggerType] = useState<FlowTrigger["type"]>(flow?.trigger.type ?? "channel.followed");
  const [minimumAmount, setMinimumAmount] = useState<number | "">(flow?.conditions[0]?.value ?? "");
  const [actions, setActions] = useState<FlowAction[]>(flow?.actions.length ? flow.actions : [{ type: "send_chat", message: "Welcome @{user} to the sleepy side!" }]);

  function changeTrigger(next: FlowTrigger["type"]) {
    setTriggerType(next);
    if (!amountFlowTriggers.has(next)) setMinimumAmount("");
  }

  function changeActionType(index: number, type: FlowAction["type"]) {
    const replacement: FlowAction = type === "send_chat"
      ? { type, message: "Welcome @{user} to the sleepy side!" }
      : type === "show_alert"
        ? { type, title: "SLEEPYFLOW ALERT", message: "{user} triggered an automation" }
        : { type, amount: 75, reason: "SleepyFlow bonus" };
    setActions((current) => current.map((action, actionIndex) => actionIndex === index ? replacement : action));
  }

  function updateAction(index: number, action: FlowAction) {
    setActions((current) => current.map((entry, actionIndex) => actionIndex === index ? action : entry));
  }

  function addAction() {
    if (actions.length < 5) setActions((current) => [...current, { type: "show_alert", title: "SLEEPYFLOW ALERT", message: "{user} triggered an automation" }]);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const conditions: FlowCondition[] = amountFlowTriggers.has(triggerType) && minimumAmount !== "" ? [{ type: "minimum_amount", value: minimumAmount }] : [];
    await onSave({ name: name.trim(), enabled: flow?.enabled ?? true, trigger: { type: triggerType }, conditions, actions });
  }

  return <div className="inline-composer flow-composer"><div><div><p className="eyebrow">Automation builder</p><h3>{flow ? `Edit ${flow.name}` : "Create SleepyFlow"}</h3></div><button className="icon-button" type="button" aria-label="Close SleepyFlow builder" onClick={onClose}><X size={17} /></button></div><form onSubmit={(event) => void submit(event)}>
    <div className="flow-builder-basics"><label>Flow name<input value={name} onChange={(event) => setName(event.target.value)} maxLength={100} placeholder="New follower welcome" required /></label><label>When this happens<select value={triggerType} onChange={(event) => changeTrigger(event.target.value as FlowTrigger["type"])}>{flowTriggerOptions.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}</select></label>{amountFlowTriggers.has(triggerType) && <label>Minimum {triggerType === "kicks.gifted" ? "KICKs" : "gifted subs"}<input type="number" value={minimumAmount} onChange={(event) => setMinimumAmount(event.target.value === "" ? "" : Number(event.target.value))} min={1} max={1_000_000_000} placeholder="No minimum" /></label>}</div>
    <div className="flow-action-stack"><div className="flow-action-heading"><div><p className="eyebrow">Then run</p><strong>{actions.length} of 5 actions</strong></div><button className="secondary-button" type="button" disabled={actions.length >= 5} onClick={addAction}><Plus size={14} /> Add action</button></div>{actions.map((action, index) => <div className="flow-action-editor" key={index}><span>{index + 1}</span><label>Action<select value={action.type} onChange={(event) => changeActionType(index, event.target.value as FlowAction["type"])}><option value="send_chat">Send chat message</option><option value="show_alert">Show OBS alert</option><option value="award_snacks">Award SleepySnacks</option></select></label>{action.type === "send_chat" && <label>Message<input value={action.message} onChange={(event) => updateAction(index, { ...action, message: event.target.value })} maxLength={500} placeholder="Welcome @{user}!" required /></label>}{action.type === "show_alert" && <><label>Alert title<input value={action.title} onChange={(event) => updateAction(index, { ...action, title: event.target.value })} maxLength={80} required /></label><label>Alert message<input value={action.message} onChange={(event) => updateAction(index, { ...action, message: event.target.value })} maxLength={200} required /></label></>}{action.type === "award_snacks" && <><label>SleepySnacks<input type="number" value={action.amount} onChange={(event) => updateAction(index, { ...action, amount: Number(event.target.value) })} min={1} max={1_000_000} required /></label><label>Ledger reason<input value={action.reason} onChange={(event) => updateAction(index, { ...action, reason: event.target.value })} minLength={2} maxLength={120} required /></label></>}<button className="icon-button danger" type="button" disabled={actions.length === 1} onClick={() => setActions((current) => current.filter((_, actionIndex) => actionIndex !== index))}><Trash2 size={14} /></button></div>)}</div>
    <div className="flow-builder-footer"><p><ShieldCheck size={15} /> Test mode never sends chat messages or spends SleepySnacks.</p><button className="primary-button" type="submit" disabled={busy}>{busy ? <RefreshCw className="spin" size={16} /> : <Save size={16} />} Save SleepyFlow</button></div>
  </form></div>;
}

function alertSampleVariables(eventType: AlertEventType): Record<string, string | number> {
  if (eventType === "channel.subscription.gifts") return { amount: 5 };
  if (eventType === "kicks.gifted") return { amount: 750 };
  if (eventType === "reward.redeemed") return { reward: "Hydrate" };
  return {};
}

function interpolateAlert(value: string, eventType: AlertEventType): string {
  const variables = { viewer: "SleepyKevy", ...alertSampleVariables(eventType) };
  return Object.entries(variables).reduce((output, [name, replacement]) => output.replaceAll(`{${name}}`, String(replacement)), value);
}

type CustomOverlayDraft = Pick<CustomOverlayRecord, "name" | "html" | "css" | "javascript">;
type CustomCodeTab = "html" | "css" | "javascript";

const customOverlayStarter: CustomOverlayDraft = {
  name: "My custom overlay",
  html: '<div id="sleepy-custom-message">Custom overlay ready</div>',
  css: `html, body {
  background: transparent;
}

body {
  display: grid;
  place-items: center;
  color: white;
  font: 700 48px/1.2 sans-serif;
  text-shadow: 0 4px 18px rgba(0, 0, 0, 0.8);
}`,
  javascript: `window.SleepyBot.onEvent((event) => {
  const message = document.querySelector("#sleepy-custom-message");
  if (message) message.textContent = event.message || event.title;
});`,
};

function CustomOverlayEditor({ overlay, preview, busy, onSave, onClose }: { overlay: CustomOverlayRecord | null; preview: boolean; busy: boolean; onSave: (draft: CustomOverlayDraft, target?: CustomOverlayRecord | null) => Promise<CustomOverlayRecord | null>; onClose: () => void }) {
  const [draft, setDraft] = useState<CustomOverlayDraft>(overlay ? { name: overlay.name, html: overlay.html, css: overlay.css, javascript: overlay.javascript } : customOverlayStarter);
  const [tab, setTab] = useState<CustomCodeTab>("html");
  const [fileError, setFileError] = useState("");
  const [assetNotice, setAssetNotice] = useState("");
  const [assets, setAssets] = useState<CustomOverlayAssetRecord[]>([]);
  const [assetBusy, setAssetBusy] = useState(false);
  const [confirmDeleteAsset, setConfirmDeleteAsset] = useState<string | null>(null);
  const tabLabels: Record<CustomCodeTab, string> = { html: "HTML", css: "CSS", javascript: "JavaScript" };
  const accepts: Record<CustomCodeTab, string> = { html: ".html,.htm,text/html", css: ".css,text/css", javascript: ".js,.mjs,text/javascript,application/javascript" };

  useEffect(() => {
    if (!overlay) {
      setDraft(customOverlayStarter);
      setAssets([]);
      return;
    }
    setDraft({ name: overlay.name, html: overlay.html, css: overlay.css, javascript: overlay.javascript });
    if (preview) return;
    let cancelled = false;
    setAssetBusy(true);
    api<CustomOverlayAssetRecord[]>(`/api/custom-overlays/${overlay.id}/assets`)
      .then((loaded) => { if (!cancelled) setAssets(loaded); })
      .catch((caught) => { if (!cancelled) setFileError(caught instanceof Error ? caught.message : "Could not load media files."); })
      .finally(() => { if (!cancelled) setAssetBusy(false); });
    return () => { cancelled = true; };
  }, [overlay?.id, preview]);

  async function loadFile(kind: CustomCodeTab, file: File) {
    setFileError("");
    if (file.size > 100_000) { setFileError(`${tabLabels[kind]} file must be 100 KB or smaller.`); return; }
    const content = await file.text();
    if (content.length > 100_000) { setFileError(`${tabLabels[kind]} file must be 100,000 characters or fewer.`); return; }
    setDraft((current) => ({ ...current, [kind]: content }));
    setTab(kind);
  }

  function assetSnippet(asset: CustomOverlayAssetRecord): string {
    if (asset.contentType === "video/webm") return `<video src="${asset.reference}" autoplay loop muted playsinline></video>`;
    return `<img src="${asset.reference}" alt="">`;
  }

  function draftWithAsset(asset: CustomOverlayAssetRecord): CustomOverlayDraft {
    const placeholder = asset.contentType === "video/webm" ? "{video}" : "{img}";
    const snippet = assetSnippet(asset);
    return { ...draft, html: draft.html.includes(placeholder) ? draft.html.replaceAll(placeholder, snippet) : `${draft.html.trimEnd()}\n${snippet}` };
  }

  async function insertAsset(asset: CustomOverlayAssetRecord) {
    if (!overlay) return;
    const nextDraft = draftWithAsset(asset);
    setDraft(nextDraft);
    setTab("html");
    setAssetBusy(true); setFileError(""); setAssetNotice("");
    const published = await onSave(nextDraft, overlay);
    if (published) setAssetNotice(`${asset.name} inserted and published. Connected OBS sources refresh automatically.`);
    setAssetBusy(false);
  }

  async function uploadAsset(file: File) {
    setFileError(""); setAssetNotice("");
    const extension = file.name.toLowerCase().split(".").pop();
    const inferredType = extension === "png" ? "image/png" : extension === "webp" ? "image/webp" : extension === "webm" ? "video/webm" : "";
    const contentType = ["image/png", "image/webp", "video/webm"].includes(file.type) ? file.type : inferredType;
    if (!contentType) { setFileError("Choose a PNG, WebP, or WebM file."); return; }
    if (file.size > 50 * 1024 * 1024) { setFileError("Media files must be 50 MB or smaller."); return; }
    setAssetBusy(true);
    try {
      const activeOverlay = overlay ?? await onSave(draft, null);
      if (!activeOverlay) return;
      const uploadFile = file.type === contentType ? file : new File([file], file.name, { type: contentType });
      const saved = await apiUpload<CustomOverlayAssetRecord>(`/api/custom-overlays/${activeOverlay.id}/assets?name=${encodeURIComponent(file.name)}`, uploadFile);
      setAssets((current) => [saved, ...current]);
      const nextDraft = draftWithAsset(saved);
      setDraft(nextDraft);
      setTab("html");
      const published = await onSave(nextDraft, activeOverlay);
      if (published) setAssetNotice(`${saved.name} uploaded, inserted, and published. Connected OBS sources refresh automatically.`);
    } catch (caught) { setFileError(caught instanceof Error ? caught.message : "Could not upload this media file."); }
    finally { setAssetBusy(false); }
  }

  async function copyAssetUrl(asset: CustomOverlayAssetRecord) {
    try { await navigator.clipboard.writeText(asset.browserUrl); setAssetNotice(`${asset.name} URL copied.`); }
    catch { setFileError("The browser blocked clipboard access."); }
  }

  async function deleteAsset(asset: CustomOverlayAssetRecord) {
    if (!overlay) return;
    if (confirmDeleteAsset !== asset.id) { setConfirmDeleteAsset(asset.id); return; }
    setAssetBusy(true); setFileError(""); setAssetNotice("");
    try {
      await api<{ deleted: boolean }>(`/api/custom-overlays/${overlay.id}/assets/${asset.id}`, { method: "DELETE", body: "{}" });
      setAssets((current) => current.filter((entry) => entry.id !== asset.id));
      setConfirmDeleteAsset(null);
      setAssetNotice(`${asset.name} deleted. Remove its asset reference from your code if it is still used.`);
    } catch (caught) { setFileError(caught instanceof Error ? caught.message : "Could not delete this media file."); }
    finally { setAssetBusy(false); }
  }

  return <form className="custom-overlay-editor panel" onSubmit={(event) => { event.preventDefault(); void onSave(draft); }}>
    <div className="custom-editor-heading"><div><p className="eyebrow">Custom browser source</p><h3>{overlay ? `Edit ${overlay.name}` : "Create custom overlay"}</h3></div><button className="icon-button" type="button" aria-label="Close custom overlay editor" onClick={onClose}><X size={18} /></button></div>
    <label className="custom-overlay-name">Overlay name<input value={draft.name} onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))} minLength={1} maxLength={80} required /></label>
    <div className="custom-file-grid">{(["html", "css", "javascript"] as const).map((kind) => <label className="custom-file-input" key={kind}><FileCode2 size={19} /><span><strong>{tabLabels[kind]} file</strong><small>{draft[kind].length.toLocaleString()} characters</small></span><b><Upload size={14} /> Choose file</b><input type="file" accept={accepts[kind]} onChange={(event) => { const file = event.currentTarget.files?.[0]; event.currentTarget.value = ""; if (file) void loadFile(kind, file); }} /></label>)}</div>
    <section className="custom-media-library">
      <div className="custom-media-heading"><div><p className="eyebrow">Media library</p><h4>PNG, WebP &amp; WebM files</h4><p>Up to 50 MB each. Uploading automatically creates, inserts, and publishes the media.</p></div><label className={`secondary-button custom-media-upload ${preview || assetBusy || busy ? "disabled" : ""}`}><Upload size={15} /> {assetBusy || busy ? "Working…" : "Upload media"}<input type="file" accept=".png,.webp,.webm,image/png,image/webp,video/webm" disabled={preview || assetBusy || busy} onChange={(event) => { const file = event.currentTarget.files?.[0]; event.currentTarget.value = ""; if (file) void uploadAsset(file); }} /></label></div>
      {!overlay && <p className="custom-media-help">Choose a media file and TheSleepyBot will create and publish this overlay automatically.</p>}
      {overlay && assets.length === 0 && !assetBusy && <p className="custom-media-help">No media uploaded yet. Existing <code>{"{img}"}</code> or <code>{"{video}"}</code> placeholders are replaced when you upload the matching file.</p>}
      {assets.length > 0 && <div className="custom-media-grid">{assets.map((asset) => <article key={asset.id}><span className="custom-media-icon">{asset.contentType === "video/webm" ? <Film /> : <FileImage />}</span><div><strong>{asset.name}</strong><small>{asset.contentType.replace("image/", "").replace("video/", "").toUpperCase()} · {(asset.sizeBytes / 1024 / 1024).toFixed(asset.sizeBytes >= 1024 * 1024 ? 1 : 2)} MB</small><code>{asset.reference}</code></div><div><button className="secondary-button" type="button" disabled={assetBusy} onClick={() => void insertAsset(asset)}>Insert &amp; publish</button><button className="text-button" type="button" disabled={assetBusy} onClick={() => void copyAssetUrl(asset)}><Copy size={13} /> Copy URL</button><button className={`text-button danger-text ${confirmDeleteAsset === asset.id ? "confirming" : ""}`} type="button" disabled={assetBusy} onClick={() => void deleteAsset(asset)}>{confirmDeleteAsset === asset.id ? "Confirm delete" : <><Trash2 size={13} /> Delete</>}</button></div></article>)}</div>}
    </section>
    {fileError && <div className="inline-error"><AlertTriangle size={16} /> {fileError}</div>}
    {assetNotice && <div className="inline-success"><ShieldCheck size={16} /> {assetNotice}</div>}
    <div className="custom-code-tabs" role="tablist">{(["html", "css", "javascript"] as const).map((kind) => <button className={tab === kind ? "active" : ""} type="button" role="tab" aria-selected={tab === kind} key={kind} onClick={() => setTab(kind)}>{tabLabels[kind]}</button>)}</div>
    <label className="custom-code-field">{tabLabels[tab]} code<textarea value={draft[tab]} onChange={(event) => setDraft((current) => ({ ...current, [tab]: event.target.value }))} maxLength={100_000} spellCheck={false} aria-label={`${tabLabels[tab]} code`} /></label>
    <div className="custom-editor-footer"><p><ShieldCheck size={15} /> JavaScript is isolated from your dashboard login. Use <code>window.SleepyBot.onEvent(...)</code> for live events.</p><button className="primary-button" type="submit" disabled={busy}>{busy ? <RefreshCw className="spin" size={16} /> : <Save size={16} />} {busy ? "Saving…" : "Save custom overlay"}</button></div>
  </form>;
}

function AlertStudioPage({ preview }: { preview: boolean }) {
  const [templates, setTemplates] = useState<AlertTemplateRecord[]>(preview ? demoAlertTemplates : []);
  const [customOverlays, setCustomOverlays] = useState<CustomOverlaySummary[]>([]);
  const [editingCustom, setEditingCustom] = useState<CustomOverlayRecord | null>();
  const [confirmDeleteCustom, setConfirmDeleteCustom] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<AlertEventType>("channel.followed");
  const [previewRun, setPreviewRun] = useState(0);
  const [loading, setLoading] = useState(!preview);
  const [busy, setBusy] = useState(false);
  const [customBusy, setCustomBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  async function refresh() {
    if (preview) return;
    setLoading(true); setError("");
    try {
      const [loadedTemplates, loadedCustomOverlays] = await Promise.all([
        api<AlertTemplateRecord[]>("/api/alerts/templates"),
        api<CustomOverlaySummary[]>("/api/custom-overlays"),
      ]);
      setTemplates(loadedTemplates);
      setCustomOverlays(loadedCustomOverlays);
    }
    catch (caught) { setError(caught instanceof Error ? caught.message : "Could not load Alert Studio."); }
    finally { setLoading(false); }
  }

  useEffect(() => { void refresh(); }, [preview]);

  const selected = templates.find((template) => template.eventType === selectedType);

  function updateSelected(update: (template: AlertTemplateRecord) => AlertTemplateRecord) {
    setTemplates((current) => current.map((template) => template.eventType === selectedType ? update(template) : template));
  }

  function runAlertPreview() {
    setError("");
    setNotice(`Playing ${selected?.name ?? "alert"} in the dashboard preview.`);
    setPreviewRun((current) => current + 1);
  }

  async function saveTemplate(sendToObs = false) {
    if (!selected) return;
    setBusy(true); setError(""); setNotice("");
    try {
      let saved = selected;
      if (!preview) {
        saved = await api<AlertTemplateRecord>(`/api/alerts/templates/${selected.eventType}`, { method: "PATCH", body: JSON.stringify({ name: selected.name, enabled: selected.enabled, config: selected.config }) });
        setTemplates((current) => current.map((template) => template.eventType === saved.eventType ? saved : template));
      }
      if (sendToObs) {
        const result = preview ? { delivered: 1 } : await api<{ delivered: number }>(`/api/alerts/templates/${saved.eventType}/test`, { method: "POST", body: "{}" });
        setNotice(result.delivered > 0 ? "Saved and sent to the connected OBS alert layer." : "Saved. No OBS alert layer is connected yet.");
      } else setNotice("Alert template saved.");
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not save this alert template."); }
    finally { setBusy(false); }
  }

  function summaryFromCustomOverlay(record: CustomOverlayRecord): CustomOverlaySummary {
    return { id: record.id, name: record.name, browserUrl: record.browserUrl, connectedSources: record.connectedSources, createdAt: record.createdAt, updatedAt: record.updatedAt, lastUsedAt: record.lastUsedAt };
  }

  async function editCustomOverlay(summary: CustomOverlaySummary) {
    setCustomBusy(true); setError(""); setNotice("");
    try {
      if (preview) setEditingCustom({ ...summary, html: customOverlayStarter.html, css: customOverlayStarter.css, javascript: customOverlayStarter.javascript });
      else setEditingCustom(await api<CustomOverlayRecord>(`/api/custom-overlays/${summary.id}`));
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not load this custom overlay."); }
    finally { setCustomBusy(false); }
  }

  async function saveCustomOverlay(draft: CustomOverlayDraft, target?: CustomOverlayRecord | null): Promise<CustomOverlayRecord | null> {
    setCustomBusy(true); setError(""); setNotice("");
    try {
      const targetOverlay = target === undefined ? editingCustom : target;
      let saved: CustomOverlayRecord;
      if (preview) {
        const now = new Date().toISOString();
        saved = { id: targetOverlay?.id ?? crypto.randomUUID(), ...draft, browserUrl: targetOverlay?.browserUrl ?? `https://thesleepybot.pages.dev/api/custom-overlay/preview-${crypto.randomUUID()}`, connectedSources: 0, createdAt: targetOverlay?.createdAt ?? now, updatedAt: now, lastUsedAt: targetOverlay?.lastUsedAt ?? null };
      } else {
        saved = await api<CustomOverlayRecord>(targetOverlay ? `/api/custom-overlays/${targetOverlay.id}` : "/api/custom-overlays", { method: targetOverlay ? "PATCH" : "POST", body: JSON.stringify(draft) });
      }
      const summary = summaryFromCustomOverlay(saved);
      setCustomOverlays((current) => current.some((overlay) => overlay.id === summary.id) ? current.map((overlay) => overlay.id === summary.id ? summary : overlay) : [summary, ...current]);
      setEditingCustom(saved);
      setNotice(`${saved.name} saved and published. Connected OBS sources refresh automatically.`);
      return saved;
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not save the custom overlay."); return null; }
    finally { setCustomBusy(false); }
  }

  async function copyCustomOverlay(summary: CustomOverlaySummary) {
    setError(""); setNotice("");
    try { await navigator.clipboard.writeText(summary.browserUrl); setNotice(`${summary.name} URL copied. Paste it into an OBS Browser source.`); }
    catch { setError("The browser blocked clipboard access. Select and copy the visible URL instead."); }
  }

  async function rotateCustomOverlay(summary: CustomOverlaySummary) {
    setCustomBusy(true); setError(""); setNotice("");
    try {
      const updated = preview
        ? { ...summary, browserUrl: summary.browserUrl.replace(/[^/]+$/, crypto.randomUUID()), connectedSources: 0, updatedAt: new Date().toISOString(), lastUsedAt: null }
        : summaryFromCustomOverlay(await api<CustomOverlayRecord>(`/api/custom-overlays/${summary.id}/rotate`, { method: "POST", body: "{}" }));
      setCustomOverlays((current) => current.map((overlay) => overlay.id === summary.id ? updated : overlay));
      setNotice(`${summary.name} URL rotated. Replace its old URL in OBS.`);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not rotate the custom overlay URL."); }
    finally { setCustomBusy(false); }
  }

  async function deleteCustomOverlay(summary: CustomOverlaySummary) {
    if (confirmDeleteCustom !== summary.id) { setConfirmDeleteCustom(summary.id); return; }
    setCustomBusy(true); setError(""); setNotice("");
    try {
      if (!preview) await api<{ deleted: boolean }>(`/api/custom-overlays/${summary.id}`, { method: "DELETE", body: "{}" });
      setCustomOverlays((current) => current.filter((overlay) => overlay.id !== summary.id));
      if (editingCustom?.id === summary.id) setEditingCustom(undefined);
      setConfirmDeleteCustom(null);
      setNotice(`${summary.name} deleted. Its OBS URL no longer works.`);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not delete the custom overlay."); }
    finally { setCustomBusy(false); }
  }

  if (!selected) return <ModulePage eyebrow="Broadcast design" title="Alert Studio" description="Design and preview event alerts for follows, subscriptions, gifts, KICKs, and reward redemptions."><div className="panel"><EmptyState icon={<BellRing />} title={loading ? "Loading Alert Studio…" : "Alert templates unavailable"} text={error || "Refresh the page to try again."} /></div></ModulePage>;

  const previewDuration = Math.min(12, Math.max(2, selected.config.durationSeconds || 6));
  const alertStyle = { "--alert-accent": selected.config.accentColor, "--alert-duration": `${previewDuration}s` } as CSSProperties;
  return <ModulePage eyebrow="Broadcast design" title="Alert Studio" description="Design and preview event alerts for follows, subscriptions, gifts, KICKs, and reward redemptions." action={<button className="primary-button" type="button" onClick={runAlertPreview}><Play size={17} /> Preview alert</button>}>
    {error && <div className="inline-error"><AlertTriangle size={16} /> {error}</div>}
    {notice && <div className="inline-success"><ShieldCheck size={16} /> {notice}</div>}
    <div className="alert-template-tabs">{templates.map((template) => <button className={template.eventType === selectedType ? "active" : ""} key={template.eventType} onClick={() => { setSelectedType(template.eventType); setNotice(""); setError(""); }}><span className={template.enabled ? "online-dot" : "offline-dot"} /><strong>{template.eventLabel}</strong><small>{template.name}</small></button>)}</div>
    <div className="alert-layout"><div className="alert-preview"><div className="preview-toolbar"><span><i /> OBS PREVIEW · 1920×1080</span><SlidersHorizontal size={17} /></div><div className={`alert-stage ${previewRun > 0 ? "playing" : ""}`} data-animation={selected.config.animation} key={`${selected.eventType}-${previewRun}`} style={alertStyle}><div className="alert-stars" /><div className="alert-content">{selected.config.showLogo && <img src="/assets/thesleepybot-mark.png" alt="" />}<p>{interpolateAlert(selected.config.label, selected.eventType)}</p><h2>SleepyKevy</h2><span>{interpolateAlert(selected.config.message, selected.eventType)}</span></div></div></div><form className="panel alert-controls" onSubmit={(event) => { event.preventDefault(); void saveTemplate(); }}><SectionHeading eyebrow="Selected template" title={selected.name} action={<Toggle checked={selected.enabled} disabled={busy} onChange={() => updateSelected((template) => ({ ...template, enabled: !template.enabled }))} />} /><label>Template name<input value={selected.name} onChange={(event) => updateSelected((template) => ({ ...template, name: event.target.value }))} minLength={1} maxLength={80} required /></label><label>Alert label<input value={selected.config.label} onChange={(event) => updateSelected((template) => ({ ...template, config: { ...template.config, label: event.target.value } }))} minLength={1} maxLength={40} required /></label><label>Message<input value={selected.config.message} onChange={(event) => updateSelected((template) => ({ ...template, config: { ...template.config, message: event.target.value } }))} minLength={1} maxLength={160} required /></label><p className="field-hint">Tokens: {"{viewer}"}{selected.eventType === "channel.subscription.gifts" || selected.eventType === "kicks.gifted" ? " · {amount}" : selected.eventType === "reward.redeemed" ? " · {reward}" : ""}</p><div className="control-row"><label>Duration<input type="number" value={selected.config.durationSeconds} onChange={(event) => updateSelected((template) => ({ ...template, config: { ...template.config, durationSeconds: Number(event.target.value) } }))} min={2} max={12} required /></label><label>Accent color<span className="color-input"><input type="color" value={selected.config.accentColor} onChange={(event) => updateSelected((template) => ({ ...template, config: { ...template.config, accentColor: event.target.value.toUpperCase() } }))} /><code>{selected.config.accentColor.toUpperCase()}</code></span></label></div><label>Animation<select value={selected.config.animation} onChange={(event) => updateSelected((template) => ({ ...template, config: { ...template.config, animation: event.target.value as AlertTemplateRecord["config"]["animation"] } }))}><option value="moonrise">Moonrise</option><option value="pulse">Neon pulse</option><option value="drift">Dream drift</option></select></label><div className="alert-toggle-row"><div><strong>Show TheSleepyBot mark</strong><p>Uses the existing logo without changing its artwork.</p></div><Toggle checked={selected.config.showLogo} onChange={() => updateSelected((template) => ({ ...template, config: { ...template.config, showLogo: !template.config.showLogo } }))} /></div><div className="alert-control-actions"><button className="secondary-button" type="button" disabled={busy} onClick={() => void saveTemplate(true)}><MonitorUp size={16} /> Save & test in OBS</button><button className="primary-button" type="submit" disabled={busy}>{busy ? <RefreshCw className="spin" size={16} /> : <Save size={16} />} Save template</button></div></form></div>
    <section className="custom-overlay-section">
      <div className="custom-overlay-heading"><div><p className="eyebrow">Custom code</p><h2>HTML, CSS &amp; JavaScript uploader</h2><p>Upload existing files or paste and edit code, then use the private transparent URL in OBS.</p></div><button className="primary-button" type="button" disabled={customBusy} onClick={() => { setEditingCustom(null); setConfirmDeleteCustom(null); setError(""); setNotice(""); }}><Plus size={16} /> New custom overlay</button></div>
      <div className="custom-security-callout"><ShieldCheck size={22} /><div><strong>Isolated custom JavaScript</strong><p>Custom code runs on a separate transparent browser-source page and cannot read your dashboard login. Each overlay gets its own private OBS URL.</p></div></div>
      {editingCustom !== undefined && <CustomOverlayEditor overlay={editingCustom} preview={preview} busy={customBusy} onSave={saveCustomOverlay} onClose={() => setEditingCustom(undefined)} />}
      <div className="custom-overlay-grid">
        {customOverlays.map((summary) => <article className="custom-overlay-card panel" key={summary.id}>
          <div className="custom-overlay-card-head"><span className="module-icon"><Code2 /></span><span className={`state-pill ${summary.connectedSources > 0 ? "online" : ""}`}><i />{summary.connectedSources > 0 ? `${summary.connectedSources} connected` : "Ready"}</span></div>
          <h3>{summary.name}</h3><p>Transparent custom browser source with uploaded HTML, CSS, and JavaScript.</p>
          <code className="custom-overlay-url" title={summary.browserUrl}>{summary.browserUrl}</code>
          <div className="custom-overlay-meta"><span>Updated {formatAgo(summary.updatedAt)}</span><span>Last opened {formatAgo(summary.lastUsedAt)}</span></div>
          <div className="custom-overlay-actions"><button className="primary-button" type="button" disabled={customBusy} onClick={() => void copyCustomOverlay(summary)}><Copy size={14} /> Copy URL</button><button className="secondary-button" type="button" disabled={customBusy} onClick={() => window.open(summary.browserUrl, "_blank", "noopener,noreferrer")}><ExternalLink size={14} /> Open preview</button><button className="secondary-button" type="button" disabled={customBusy} onClick={() => void editCustomOverlay(summary)}><Pencil size={14} /> Edit code</button><button className="text-button" type="button" disabled={customBusy} onClick={() => void rotateCustomOverlay(summary)}><RefreshCw className={customBusy ? "spin" : ""} size={14} /> Rotate URL</button><button className={`text-button danger-text ${confirmDeleteCustom === summary.id ? "confirming" : ""}`} type="button" disabled={customBusy} onClick={() => void deleteCustomOverlay(summary)}>{confirmDeleteCustom === summary.id ? "Confirm delete" : <><Trash2 size={14} /> Delete</>}</button></div>
        </article>)}
        {customOverlays.length === 0 && <div className="panel custom-overlay-empty"><Code2 /><strong>No custom overlays yet</strong><p>Create one to upload HTML, CSS, and JavaScript and receive a private transparent OBS URL.</p></div>}
      </div>
    </section>
  </ModulePage>;
}

const widgetIcons: Record<WidgetType, typeof Activity> = { alerts: BellRing, events: Activity, leaderboard: Coins, giveaway: Trophy };

function WidgetsPage({ preview }: { preview: boolean }) {
  const [widgets, setWidgets] = useState<WidgetRecord[]>(preview ? demoWidgets : []);
  const [loading, setLoading] = useState(!preview);
  const [busyType, setBusyType] = useState<WidgetType | "all" | null>(null);
  const [confirmRotateAll, setConfirmRotateAll] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  async function refresh() {
    if (preview) return;
    setLoading(true); setError("");
    try { setWidgets(await api<WidgetRecord[]>("/api/widgets")); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "Could not load OBS Widgets."); }
    finally { setLoading(false); }
  }

  useEffect(() => { void refresh(); }, [preview]);

  async function rotate(type: WidgetType) {
    setBusyType(type); setError(""); setNotice("");
    try {
      if (preview) setWidgets((current) => current.map((widget) => widget.type === type ? { ...widget, browserUrl: widget.browserUrl.replace(/[^/]+$/, crypto.randomUUID()), connectedSources: 0, createdAt: new Date().toISOString(), lastUsedAt: null } : widget));
      else {
        const updated = await api<WidgetRecord>(`/api/widgets/${type}/rotate`, { method: "POST", body: "{}" });
        setWidgets((current) => current.map((widget) => widget.type === type ? updated : widget));
      }
      setNotice(`${widgets.find((widget) => widget.type === type)?.label ?? "Widget"} URL rotated. Replace the old URL in OBS.`);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not rotate this widget URL."); }
    finally { setBusyType(null); }
  }

  async function rotateAll() {
    if (!confirmRotateAll) { setConfirmRotateAll(true); return; }
    setBusyType("all"); setError(""); setNotice("");
    try {
      if (preview) setWidgets((current) => current.map((widget) => ({ ...widget, browserUrl: widget.browserUrl.replace(/[^/]+$/, crypto.randomUUID()), connectedSources: 0, createdAt: new Date().toISOString(), lastUsedAt: null })));
      else setWidgets(await api<WidgetRecord[]>("/api/widgets/rotate-all", { method: "POST", body: "{}" }));
      setNotice("All four widget URLs were rotated. Replace every old URL in OBS.");
      setConfirmRotateAll(false);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not rotate the widget URLs."); }
    finally { setBusyType(null); }
  }

  async function copyUrl(widget: WidgetRecord) {
    setError(""); setNotice("");
    try {
      await navigator.clipboard.writeText(widget.browserUrl);
      setNotice(`${widget.label} URL copied. Paste it into an OBS Browser source.`);
    } catch { setError("The browser blocked clipboard access. Select and copy the visible URL instead."); }
  }

  return <ModulePage eyebrow="Browser sources" title="OBS Widgets" description="Secure, rotatable browser-source URLs with automatic real-time reconnection." action={<button className={confirmRotateAll ? "secondary-button danger-button" : "secondary-button"} disabled={loading || busyType !== null} onClick={() => void rotateAll()}><RefreshCw className={busyType === "all" ? "spin" : ""} size={17} /> {confirmRotateAll ? "Confirm rotate all" : "Rotate all URLs"}</button>}>
    {error && <div className="inline-error"><AlertTriangle size={16} /> {error}</div>}
    {notice && <div className="inline-success"><ShieldCheck size={16} /> {notice}</div>}
    <div className="widget-callout"><MonitorUp size={22} /><div><strong>OBS browser-source mode</strong><p>Copy a private URL into an OBS Browser source, set it to 1920×1080, and leave “Shutdown source when not visible” off.</p></div></div>
    <div className="card-grid two">{widgets.map((widget) => { const Icon = widgetIcons[widget.type]; return <article className="widget-card" key={widget.type}><span className="module-icon"><Icon /></span><div className="widget-details"><small>{widget.connectedSources > 0 ? `${widget.connectedSources} connected` : "Ready"}</small><h3>{widget.label}</h3><p>{widget.description}</p><code title={widget.browserUrl}>{widget.browserUrl}</code><span>Last opened: {formatAgo(widget.lastUsedAt)}</span></div><div className="widget-actions"><button className="primary-button" disabled={busyType !== null} onClick={() => void copyUrl(widget)}><Copy size={15} /> Copy URL</button><button className="secondary-button" disabled={busyType !== null} onClick={() => window.open(widget.browserUrl, "_blank", "noopener,noreferrer")}><ExternalLink size={15} /> Open preview</button><button className="text-button danger-text" disabled={busyType !== null} onClick={() => void rotate(widget.type)}><RefreshCw className={busyType === widget.type ? "spin" : ""} size={14} /> Rotate URL</button></div></article>; })}{widgets.length === 0 && <div className="panel"><EmptyState icon={<MonitorUp />} title={loading ? "Creating secure widget URLs…" : "No widget URLs available"} text="Refresh the page to try again." /></div>}</div>
  </ModulePage>;
}

function SettingsPage({ preview }: { preview: boolean }) {
  const [summary, setSummary] = useState<DashboardSummary>(preview ? demoSummary : disconnectedSummary);
  const [loading, setLoading] = useState(!preview);
  const [connecting, setConnecting] = useState(false);
  const [testModeBusy, setTestModeBusy] = useState(false);
  const [error, setError] = useState("");

  async function refreshConnection() {
    if (preview) return;
    setLoading(true);
    setError("");
    try {
      setSummary(await api<DashboardSummary>("/api/dashboard"));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not check the Kick connection.");
    } finally {
      setLoading(false);
    }
  }

  async function connectKick() {
    setConnecting(true);
    setError("");
    try {
      const result = await api<{ url: string }>("/api/kick/connect");
      window.location.assign(result.url);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not start the Kick connection.");
      setConnecting(false);
    }
  }

  async function setOfflineTestMode(enabled: boolean) {
    if (preview) {
      setSummary((current) => ({ ...current, channel: { ...current.channel, offlineTestMode: enabled, botActive: enabled || current.channel.live } }));
      return;
    }
    setTestModeBusy(true);
    setError("");
    try {
      const result = await api<{ offlineTestMode: boolean }>("/api/channel/offline-test-mode", { method: "PATCH", body: JSON.stringify({ enabled }) });
      setSummary((current) => ({ ...current, channel: { ...current.channel, offlineTestMode: result.offlineTestMode, botActive: result.offlineTestMode || current.channel.live } }));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not change offline test mode.");
    } finally {
      setTestModeBusy(false);
    }
  }

  useEffect(() => {
    if (preview) return;
    void refreshConnection();
  }, [preview]);

  const connected = summary.channel.kickConnected;
  const accountLabel = connected && summary.channel.slug ? `@${summary.channel.slug}` : "No Kick account connected";
  const connectionDetail = connected
    ? summary.health.lastEventAt
      ? `Connected as TheSleepyBot · last webhook ${formatAgo(summary.health.lastEventAt)}`
      : "Connected as TheSleepyBot · waiting for the first verified webhook"
    : loading
      ? "Checking your real connection status…"
      : "Authorize the Kick account you want TheSleepyBot to manage.";

  return <ModulePage eyebrow="Private configuration" title="Settings" description="Manage your Kick connection, live behavior, security, retention, and channel defaults." action={<button className="secondary-button" disabled={loading} onClick={() => void refreshConnection()}><RefreshCw className={loading ? "spin" : ""} size={17} /> Refresh connection</button>}><div className="settings-grid"><section className="panel"><SectionHeading eyebrow="Integration" title="Kick channel" /><div className="connection-card" data-connection-source="live"><span className="kick-mark">K</span><div><strong>{accountLabel}</strong><p>{connectionDetail}</p></div><StatePill state={connected ? "online" : "offline"} /></div>{error && <div className="inline-error"><AlertTriangle size={16} /> {error}</div>}{!connected && !preview && <button className="primary-button full" disabled={loading || connecting} onClick={() => void connectKick()}><PlugZap size={16} /> {connecting ? "Opening Kick…" : "Connect Kick account"}</button>}<SettingRow title="Offline command test mode" detail="Lets custom commands reply while offline. Timers, moderation, alerts, and SleepySnacks remain asleep." enabled={summary.channel.offlineTestMode} disabled={!connected || testModeBusy} onChange={(enabled) => void setOfflineTestMode(enabled)} /><SettingRow title="Run only while channel is live" detail="Normal bot activity automatically returns to live-only operation when test mode is off." enabled /><SettingRow title="Allow approved Kick moderator commands" detail="Moderators receive chat command access, never dashboard access." enabled /></section><section className="panel"><SectionHeading eyebrow="Data & security" title="Privacy controls" /><SettingRow title="90-day chat retention" detail="Full messages are removed automatically after 90 days." enabled /><SettingRow title="Keep moderation incidents" detail="Incidents remain until you delete them or honor an erasure request." enabled /><SettingRow title="Two-factor authentication" detail="Optional TOTP protection for the owner login." /><button className="secondary-button full"><KeyRound size={16} /> Change password</button></section></div></ModulePage>;
}

function ModulePage({ eyebrow, title, description, action, children }: { eyebrow: string; title: string; description: string; action?: ReactNode; children: ReactNode }) {
  return <div className="module-page"><div className="module-hero"><div><p className="eyebrow">{eyebrow}</p><h2>{title}</h2><p>{description}</p></div>{action}</div>{children}</div>;
}

function SectionHeading({ eyebrow, title, action }: { eyebrow: string; title: string; action?: ReactNode }) {
  return <div className="section-heading"><div><p className="eyebrow">{eyebrow}</p><h2>{title}</h2></div>{action}</div>;
}

function FilterBar({ placeholder, chips }: { placeholder: string; chips: string[] }) {
  return <div className="filter-bar"><label><ListFilter size={17} /><input placeholder={placeholder} /></label><div>{chips.map((chip, index) => <button className={index === 0 ? "active" : ""} key={chip}>{chip}</button>)}</div></div>;
}

function CommandComposer({ command, busy, onSave, onClose }: { command?: CommandRecord; busy: boolean; onSave: (draft: Omit<CommandRecord, "id" | "uses">) => Promise<void>; onClose: () => void }) {
  const [name, setName] = useState(command?.name ?? "");
  const [aliases, setAliases] = useState(command?.aliases.join(", ") ?? "");
  const [response, setResponse] = useState(command?.response ?? "");
  const [permission, setPermission] = useState<CommandRecord["permission"]>(command?.permission ?? "everyone");
  const [cooldownSeconds, setCooldownSeconds] = useState(command?.cooldownSeconds ?? 30);
  const [cost, setCost] = useState(command?.cost ?? 0);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const cleanName = name.trim().replace(/^!+/, "");
    const cleanAliases = [...new Set(aliases.split(",").map((alias) => alias.trim().replace(/^!+/, "").toLowerCase()).filter(Boolean))];
    await onSave({ name: cleanName, aliases: cleanAliases, response: response.trim(), enabled: command?.enabled ?? true, permission, cooldownSeconds, cost });
  }

  return <div className="inline-composer"><div><h3>{command ? `Edit !${command.name}` : "Create command"}</h3><button className="icon-button" type="button" aria-label="Close command form" onClick={onClose}><X size={18} /></button></div><form onSubmit={(event) => void submit(event)}><label>Command name<input value={name} onChange={(event) => setName(event.target.value)} placeholder="hello" pattern="!?[A-Za-z0-9_]{1,32}" maxLength={33} required /></label><label>Aliases, comma separated<input value={aliases} onChange={(event) => setAliases(event.target.value)} placeholder="hi, hey" /></label><label>Response message<input value={response} onChange={(event) => setResponse(event.target.value)} placeholder="Hello {user}!" maxLength={500} required /></label><label>Access<select value={permission} onChange={(event) => setPermission(event.target.value as CommandRecord["permission"])}><option value="everyone">Everyone</option><option value="subscriber">Subscribers</option><option value="moderator">Moderators</option><option value="owner">Owner only</option></select></label><label>Cooldown seconds<input type="number" value={cooldownSeconds} onChange={(event) => setCooldownSeconds(Number(event.target.value))} min={0} max={86_400} required /></label><label>SleepySnacks cost<input type="number" value={cost} onChange={(event) => setCost(Number(event.target.value))} min={0} max={1_000_000} required /></label><button className="primary-button" type="submit" disabled={busy}>{busy ? <RefreshCw className="spin" size={16} /> : <Save size={16} />} {busy ? "Saving…" : "Save command"}</button></form></div>;
}

function VariableComposer({ variable, busy, onSave, onClose }: { variable?: VariableRecord; busy: boolean; onSave: (draft: Omit<VariableRecord, "id">) => Promise<void>; onClose: () => void }) {
  const [name, setName] = useState(variable?.name ?? "");
  const [value, setValue] = useState(variable?.value ?? "");

  async function submit(event: FormEvent) {
    event.preventDefault();
    await onSave({ name: name.trim().replace(/[{}]/g, ""), value: value.trim() });
  }

  return <div className="inline-composer variable-composer"><div><h3>{variable ? `Edit {${variable.name}}` : "Create variable"}</h3><button className="icon-button" type="button" aria-label="Close variable form" onClick={onClose}><X size={18} /></button></div><form onSubmit={(event) => void submit(event)}><label>Variable name<input value={name} onChange={(event) => setName(event.target.value)} placeholder="discord" pattern="\{?[A-Za-z0-9_]{1,32}\}?" maxLength={34} required /></label><label>Replacement value<input value={value} onChange={(event) => setValue(event.target.value)} placeholder="https://discord.gg/your-server" maxLength={500} required /></label><button className="primary-button" type="submit" disabled={busy}>{busy ? <RefreshCw className="spin" size={16} /> : <Save size={16} />} {busy ? "Saving…" : "Save variable"}</button></form></div>;
}

function Toggle({ checked, disabled = false, onChange }: { checked: boolean; disabled?: boolean; onChange: () => void }) {
  return <button className={`toggle ${checked ? "on" : ""}`} role="switch" aria-checked={checked} disabled={disabled} onClick={onChange}><span /></button>;
}

function MiniStat({ label, value, change }: { label: string; value: string; change: string }) {
  return <article className="mini-stat"><span>{label}</span><strong>{value}</strong><small>{change}</small></article>;
}

function SettingRow({ title, detail, enabled = false, disabled = false, onChange }: { title: string; detail: string; enabled?: boolean; disabled?: boolean; onChange?: (enabled: boolean) => void }) {
  const [on, setOn] = useState(enabled);
  const checked = onChange ? enabled : on;
  return <div className="setting-row"><div><strong>{title}</strong><p>{detail}</p></div><Toggle checked={checked} disabled={disabled} onChange={() => onChange ? onChange(!checked) : setOn(!on)} /></div>;
}

function EmptyState({ icon, title, text }: { icon: ReactNode; title: string; text: string }) {
  return <div className="empty-state"><span>{icon}</span><strong>{title}</strong><p>{text}</p></div>;
}

interface OverlayEvent {
  type: string;
  title: string;
  viewer?: string;
  message?: string;
  data?: { alert?: Partial<AlertTemplateRecord["config"]> };
  createdAt?: string;
}

interface OverlaySnapshot {
  alert?: OverlayEvent | null;
  events?: OverlayEvent[];
  leaderboard?: SnackLeaderboardRow[];
  giveaway?: GiveawayRecord | null;
}

function parsedOverlayEvent(value: unknown): OverlayEvent | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const candidate = value as { type?: unknown; title?: unknown; viewer?: unknown; message?: unknown; data?: unknown; createdAt?: unknown };
  if (typeof candidate.type !== "string" || typeof candidate.title !== "string") return null;
  return { type: candidate.type, title: candidate.title, viewer: typeof candidate.viewer === "string" ? candidate.viewer : undefined, message: typeof candidate.message === "string" ? candidate.message : undefined, data: candidate.data && typeof candidate.data === "object" && !Array.isArray(candidate.data) ? candidate.data as { alert?: Partial<AlertTemplateRecord["config"]> } : undefined, createdAt: typeof candidate.createdAt === "string" ? candidate.createdAt : undefined };
}

function OverlayPage() {
  const parts = window.location.pathname.split("/").filter(Boolean);
  const rawKind = parts[1] ?? "alerts";
  const kind: WidgetType = rawKind === "events" || rawKind === "leaderboard" || rawKind === "giveaway" ? rawKind : "alerts";
  const token = parts[2] ?? "";
  const [connected, setConnected] = useState(false);
  const [event, setEvent] = useState<OverlayEvent | null>(null);
  const [recentEvents, setRecentEvents] = useState<OverlayEvent[]>([]);
  const [leaderboard, setLeaderboard] = useState<SnackLeaderboardRow[]>([]);
  const [giveaway, setGiveaway] = useState<GiveawayRecord | null>(null);

  useEffect(() => {
    if (!token) return;
    const apiOrigin = apiBaseUrl() || window.location.origin;
    const wsOrigin = apiOrigin.replace(/^http/, "ws");
    let disposed = false;
    let socket: WebSocket | null = null;
    let reconnectTimer: number | undefined;
    let heartbeatTimer: number | undefined;
    let attempts = 0;

    async function refreshSnapshot() {
      try {
        const response = await fetch(`${apiOrigin}/api/overlay/${kind}/${encodeURIComponent(token)}/data`, { credentials: "omit", cache: "no-store" });
        if (!response.ok) return;
        const payload = await response.json() as { ok?: unknown; data?: unknown };
        if (payload.ok !== true || !payload.data || typeof payload.data !== "object" || Array.isArray(payload.data)) return;
        const snapshot = payload.data as OverlaySnapshot;
        if (kind === "events" && Array.isArray(snapshot.events)) setRecentEvents(snapshot.events.map(parsedOverlayEvent).filter((item): item is OverlayEvent => item !== null).slice(-5));
        if (kind === "leaderboard" && Array.isArray(snapshot.leaderboard)) setLeaderboard(snapshot.leaderboard.slice(0, 5));
        if (kind === "giveaway") setGiveaway(snapshot.giveaway ?? null);
      } catch { /* The WebSocket reconnect loop remains active. */ }
    }

    function connect() {
      if (disposed) return;
      socket = new WebSocket(`${wsOrigin}/api/overlay/${kind}/${encodeURIComponent(token)}/socket`);
      socket.addEventListener("open", () => {
        attempts = 0;
        setConnected(true);
        heartbeatTimer = window.setInterval(() => { if (socket?.readyState === WebSocket.OPEN) socket.send("ping"); }, 25_000);
      });
      socket.addEventListener("close", () => {
        setConnected(false);
        if (heartbeatTimer !== undefined) window.clearInterval(heartbeatTimer);
        if (!disposed) {
          const delay = Math.min(15_000, 1_000 * (2 ** Math.min(attempts, 4)));
          attempts += 1;
          reconnectTimer = window.setTimeout(connect, delay);
        }
      });
      socket.addEventListener("message", (message) => {
        try {
          const candidate = parsedOverlayEvent(JSON.parse(String(message.data)) as unknown);
          if (!candidate || candidate.type === "system") return;
          if (kind === "alerts" && candidate.type === "alert") setEvent(candidate);
          if (kind === "events") setRecentEvents((current) => [...current, candidate].slice(-5));
          if (kind === "giveaway" && candidate.type === "giveaway") void refreshSnapshot();
          if (kind === "leaderboard" && candidate.type === "leaderboard") void refreshSnapshot();
        } catch { /* Ignore malformed realtime messages and pong replies. */ }
      });
    }

    void refreshSnapshot();
    const snapshotTimer = window.setInterval(() => void refreshSnapshot(), 30_000);
    connect();
    return () => {
      disposed = true;
      window.clearInterval(snapshotTimer);
      if (heartbeatTimer !== undefined) window.clearInterval(heartbeatTimer);
      if (reconnectTimer !== undefined) window.clearTimeout(reconnectTimer);
      socket?.close(1000, "Overlay closed");
    };
  }, [kind, token]);

  const alertConfig = event?.data?.alert;
  const animation = alertConfig?.animation === "pulse" || alertConfig?.animation === "drift" ? alertConfig.animation : "moonrise";
  const accentColor = typeof alertConfig?.accentColor === "string" && /^#[0-9A-Fa-f]{6}$/.test(alertConfig.accentColor) ? alertConfig.accentColor : "#35D6FF";
  const durationSeconds = typeof alertConfig?.durationSeconds === "number" && alertConfig.durationSeconds >= 2 && alertConfig.durationSeconds <= 12 ? alertConfig.durationSeconds : 6;
  const alertStyle = { "--alert-accent": accentColor, "--alert-duration": `${durationSeconds}s` } as CSSProperties;
  return <main className="overlay-page" data-connected={connected} data-widget={kind}>
    {kind === "alerts" && event && <div className="overlay-alert" data-animation={animation} key={event.createdAt ?? `${event.title}-${event.viewer}`} style={alertStyle}>{alertConfig?.showLogo !== false && <img src="/assets/thesleepybot-mark.png" alt="" />}<p>{event.title}</p><h1>{event.viewer}</h1>{event.message && <span>{event.message}</span>}</div>}
    {kind === "events" && recentEvents.length > 0 && <section className="overlay-panel overlay-events"><header><Activity size={23} /><div><small>LIVE ACTIVITY</small><strong>TheSleepyBot events</strong></div></header>{recentEvents.map((item, index) => <div key={`${item.createdAt ?? index}-${item.title}`}><span>{item.viewer?.slice(0, 1).toUpperCase() ?? "S"}</span><p><strong>{item.viewer ?? item.title}</strong><small>{item.message ?? item.title}</small></p></div>)}</section>}
    {kind === "leaderboard" && leaderboard.length > 0 && <section className="overlay-panel overlay-board"><header><Coins size={23} /><div><small>SLEEPYSNACKS</small><strong>Community leaderboard</strong></div></header>{leaderboard.map((leader) => <div key={leader.viewerId}><b>#{leader.rank}</b><strong>{leader.username}</strong><span>{leader.balance.toLocaleString()}</span></div>)}</section>}
    {kind === "giveaway" && giveaway && <section className="overlay-panel overlay-giveaway"><Trophy size={32} /><small>{giveaway.status === "open" ? "GIVEAWAY OPEN" : giveaway.status === "completed" ? "WINNER DRAWN" : "ENTRIES CLOSED"}</small><h1>{giveaway.title}</h1><p>{giveaway.prize}</p>{giveaway.winnerUsername ? <strong>@{giveaway.winnerUsername} wins!</strong> : <strong>{giveaway.entryCount.toLocaleString()} entries · !{giveaway.entryCommand}</strong>}</section>}
  </main>;
}

export default App;
